import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import "@/App.css";
import "@/ExhibitorApplication.css";
import { BrowserRouter, Link, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowUpRight, CalendarDays, ChevronDown, ChevronRight, Diamond, Globe2, Heart, LogOut, Menu, Radio, RefreshCw, Search, ShieldCheck, Sparkles, Video, X } from "lucide-react";
import { api } from "@/api";
import Admin from "@/Admin";
import {SiteProvider, useSite, MarketingHome, ContentPage, ContentFooter, SiteImage} from "@/siteContent";
import {CommunicationCenter, ChatButton, LiveAlertPreference} from "@/communications";
import MeetingCard from "@/MeetingCard";
import Verify, { VerifiedBadge, VerificationPill } from "@/Verify";
import { AuthCallback, AuthProvider, ProtectedRoute, startGoogleSignIn, useAuth } from "@/auth";
import { BroadcastControls, liveWindowOpen, MeetingRoomModal, WatchLiveModal } from "@/video";
import {ExhibitorCommercialPanel} from "@/commercial";
import {PricingPage} from "@/PolishedPages";
import {EntitlementPanel} from "@/Entitlements";
import {exhibitorApplicationShowLabel, exhibitorApplicationShows} from "@/exhibitorShows";
import "@/BrandTheme.css";
import {ShowStatus, showPresentation} from "@/ShowPresentation";
import "@/ShowTheme.css";

const categories = ["Natural Diamonds", "Lab-Grown Diamonds", "Diamond Jewellery", "Gold Jewellery", "Gemstones"];
const applicationCategories = ["Diamond Jewellery", "Imitation Jewellery", "Gemstones", "Diamonds"];

// ---------- Data context ----------
const DataContext = createContext(null);
const useData = () => useContext(DataContext);

function useAccessKey() {
  const { user, loading } = useAuth();
  return JSON.stringify([loading, user?.user_id, user?.role, user?.exhibitor_id]);
}

function liveDeadline(booth) {
  return booth.live_closes_at ? Date.now() + (booth.live_closes_at - (booth.live_server_time ?? Date.now()/1000)) * 1000 : null;
}
const EMPTY_CATALOG = { exhibitors: [], products: [], exhibitions: [], loaded: false, error: "" };

function DataProvider({ children }) {
  const {pathname} = useLocation();
  const needsProducts = !["/", "/about", "/contact", "/pricing", "/exhibit", "/login"].includes(pathname) && !pathname.startsWith("/legal/");
  const pollLive = !["/about", "/contact", "/pricing", "/exhibit", "/login", "/admin"].includes(pathname) && !pathname.startsWith("/legal/");
  const accessKey = useAccessKey() + ":" + needsProducts;
  const { loading: authLoading } = useAuth();
  const [state, setState] = useState(EMPTY_CATALOG);
  useEffect(() => {
    if (authLoading) return;
    let alive = true;
    Promise.allSettled([api.listExhibitors(), needsProducts ? api.listProducts() : Promise.resolve([]), api.listExhibitions()])
      .then((results) => {
        if (!alive) return;
        const values = results.map((result) => result.status === "fulfilled" && Array.isArray(result.value) ? result.value : []);
        setState({ key: accessKey, exhibitors: values[0].map(row => ({...row, live_deadline: liveDeadline(row)})), products: values[1], exhibitions: values[2].map(e => ({...e, live_received_at: Date.now()})), loaded: true,
          error: results.some((result) => result.status === "rejected") ? "Some catalogue data is unavailable. Please refresh to try again." : "" });
      });
    return () => { alive = false; };
  }, [accessKey, authLoading, needsProducts]);
  // Refresh LIVE indicators only; do not reload catalogues or other business data.
  useEffect(() => {
    if (authLoading) return;
    if (!pollLive) return;
    let alive = true, timer;
    const refreshLive = async () => {
      try {
        const [rows, schedules] = await Promise.all([api.listExhibitors(), api.listExhibitions()]);
        if (!alive || !Array.isArray(rows)) return;
        const liveIds = new Set(rows.filter(row => row.live).map(row => row.id));
        setState(previous => previous.key !== accessKey ? previous : {...previous,
          exhibitions: Array.isArray(schedules) ? schedules.map(e => ({...e, live_received_at: Date.now()})) : [],
          exhibitors: previous.exhibitors.map(row => ({...row, live: liveIds.has(row.id), live_exhibition_id: rows.find(e => e.id === row.id)?.live_exhibition_id, live_deadline: liveDeadline(rows.find(e => e.id === row.id) || {})}))});
      } catch {
        if (alive) setState(previous => previous.key !== accessKey ? previous : {...previous,
          exhibitions: previous.exhibitions.map(e => ({...e, live_allowed: false, live_schedule_state: "UNAVAILABLE"})),
          exhibitors: previous.exhibitors.map(row => ({...row, live: false}))});
      } finally { if (alive) timer = setTimeout(refreshLive, 15000); }
    };
    timer = setTimeout(refreshLive, 15000);
    return () => { alive = false; clearTimeout(timer); };
  }, [accessKey, authLoading, pollLive]);
  useEffect(() => {
    const deadlines = state.exhibitors.filter(e => e.live && e.live_deadline).map(e => e.live_deadline);
    if (!deadlines.length) return;
    const timer = setTimeout(() => setState(previous => ({...previous, exhibitors: previous.exhibitors.map(e =>
      e.live_deadline && e.live_deadline <= Date.now() ? {...e, live:false} : e)})), Math.max(0, Math.min(...deadlines)-Date.now()));
    return () => clearTimeout(timer);
  }, [state.exhibitors]);
  // Account changes must hide old data in this render, before effects run.
  const value = state.key === accessKey ? state : EMPTY_CATALOG;
  return <DataContext.Provider value={value}>
    {!value.loaded && <p role="status" className="content-section">Loading catalogue…</p>}
    {value.error && <p role="alert" className="content-section">{value.error}</p>}
    {children}
  </DataContext.Provider>;
}

// ---------- Shortlist context (auth-aware, persists per user) ----------
const ShortlistContext = createContext(null);
const useShortlist = () => useContext(ShortlistContext);

function ShortlistProvider({ children }) {
  const accessKey = useAccessKey();
  return <ScopedShortlistProvider key={accessKey}>{children}</ScopedShortlistProvider>;
}

function ScopedShortlistProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]); // hydrated shortlist rows from /api/me/shortlist
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); return; }
    setLoading(true);
    try { setItems(await api.listShortlist()); }
    catch { /* keep prior items */ }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const key = (kind, id) => `${kind}:${id}`;
  const savedSet = useMemo(
    () => new Set(items.map((row) => key(row.kind, row.item_id))),
    [items]
  );

  const isSaved = useCallback((kind, id) => savedSet.has(key(kind, id)), [savedSet]);

  const toggle = useCallback(async (kind, id) => {
    if (!user) return { needsAuth: true };
    const currentlySaved = savedSet.has(key(kind, id));
    // optimistic
    if (currentlySaved) {
      setItems((prev) => prev.filter((r) => !(r.kind === kind && r.item_id === id)));
      try { await api.removeShortlist(kind, id); }
      catch { refresh(); }
    } else {
      const stub = { id: `tmp-${id}`, kind, item_id: id, created_at: new Date().toISOString(), product: null, exhibitor: null };
      setItems((prev) => [stub, ...prev]);
      try { await api.addShortlist({ kind, item_id: id }); refresh(); }
      catch { refresh(); }
    }
    return { needsAuth: false };
  }, [user, savedSet, refresh]);

  const value = useMemo(() => ({ items, loading, isSaved, toggle, refresh }), [items, loading, isSaved, toggle, refresh]);
  return <ShortlistContext.Provider value={value}>{children}</ShortlistContext.Provider>;
}

// ---------- Layout ----------
function Header() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const handleSignOut = async () => {
    // Navigate FIRST so any mounted ProtectedRoute unmounts before the user
    // is cleared — otherwise it fires its own redirect to /login and wins
    // the race against our navigate('/').
    navigate("/", { replace: true });
    try { await signOut(); } catch { /* state cleared regardless */ }
  };
  useEffect(() => setOpen(false), [location.pathname]);
  const links = [["Explore", "/explore"], ["Exhibitions", "/exhibitions"], ["Exhibitors", "/exhibitors"], ["Products", "/products"], ["Pricing", "/pricing"]];
  const workspaceHref =
    user?.role === "admin" ? "/admin"
    : user?.role === "exhibitor" ? "/dashboard/exhibitor"
    : "/dashboard/buyer";
  return (
    <header className="site-header">
      <Link className="brand" to="/" data-testid="brand-home-link">
        <span className="brand-mark"><Diamond size={17} strokeWidth={1.5}/></span>
        <span>CARATJEWEL <b>EXPO</b></span>
      </Link>
      <nav className={open ? "nav open" : "nav"}>
        {links.map(([label, href]) => (
          <Link key={href} className={location.pathname === href ? "active" : ""} to={href} data-testid={`nav-${label.toLowerCase()}-link`}>{label}</Link>
        ))}
        <Link to="/exhibit" data-testid="nav-exhibit-link">Exhibit with us <ArrowUpRight size={14}/></Link>
        <div className="nav-auth" data-testid="nav-auth-mobile">
          {user ? (
            <>
              <Link className="nav-auth-primary" to={workspaceHref} data-testid="nav-dashboard-link">
                Dashboard · <span className="role-tag">{user.role}</span>
              </Link>
              <button className="nav-auth-signout" onClick={handleSignOut} data-testid="nav-signout-button">
                <LogOut size={14}/>&nbsp;Sign out
              </button>
            </>
          ) : (
            <>
              <Link className="nav-auth-primary" to="/login" data-testid="nav-signin-link">Sign in</Link>
              <Link className="nav-auth-signout" to="/exhibit" data-testid="nav-exhibit-cta">Join the expo <ArrowUpRight size={14}/></Link>
            </>
          )}
        </div>
      </nav>
      <div className="header-actions">
        {user ? (
          <>
            <Link className="text-link" to={workspaceHref} data-testid="header-workspace-link">
              {user.name?.split(" ")[0] || "Dashboard"} · <span className="role-tag" data-testid="header-user-role">{user.role}</span>
            </Link>
            <button className="text-link header-signout" onClick={handleSignOut} data-testid="header-signout-button">
              <LogOut size={13}/>&nbsp;Sign out
            </button>
          </>
        ) : (
          <>
            <Link className="text-link" to="/login" data-testid="header-login-link">Sign in</Link>
            <Link className="button button-dark compact" to="/exhibit" data-testid="header-exhibit-button">Join the expo</Link>
          </>
        )}
      </div>
      <button className="icon-button mobile-menu" onClick={() => setOpen(!open)} aria-label={open ? "Close navigation" : "Open navigation"} aria-expanded={open} data-testid="mobile-menu-button">{open ? <X/> : <Menu/>}</button>
      <style>{`
        .role-tag{font:9px 'DM Mono',monospace;letter-spacing:.08em;text-transform:uppercase;color:#638d85}
        .header-signout{background:none;border:0;padding:0;font-family:inherit;cursor:pointer;display:inline-flex;align-items:center;color:#6d6d69}
        .header-signout:hover{color:var(--ink)}
        .nav-auth{display:none}
        @media(max-width:850px){
          .nav.open .nav-auth{display:flex;flex-direction:column;gap:14px;width:100%;margin-top:14px;padding-top:18px;border-top:1px solid var(--line)}
          .nav-auth-primary{font-size:13px;color:var(--ink);display:inline-flex;align-items:center;gap:6px}
          .nav-auth-signout{background:none;border:1px solid var(--line);padding:12px 14px;font:600 11px 'Manrope',sans-serif;letter-spacing:.05em;color:var(--ink);display:inline-flex;align-items:center;gap:4px;width:fit-content;cursor:pointer;font-family:inherit}
        }
      `}</style>
    </header>
  );
}

// ---------- Home ----------
function Home() {
  const {exhibitors, exhibitions} = useData();
  return <MarketingHome exhibitors={exhibitors} exhibitions={exhibitions}/>;
}

function PageIntro({ eyebrow, title, children }) {
  return <div className="page-intro"><div className="eyebrow">{eyebrow}</div><h1>{title}</h1>{children && <p>{children}</p>}</div>;
}

// ---------- Directory pages ----------
const categoryMap = { "Natural Diamonds": "Natural Diamond", "Lab-Grown Diamonds": "Lab-Grown Diamond", "Diamond Jewellery": "Diamond Jewellery", "Gold Jewellery": "Gold Jewellery", Gemstones: "Gemstone" };

function useProductFilter() {
  const { products } = useData();
  const [selected, setSelected] = useState("All categories");
  const [query, setQuery] = useState("");
  const filtered = products.filter(p =>
    (selected === "All categories" || p.type === categoryMap[selected]) &&
    `${p.name} ${p.exhibitor}`.toLowerCase().includes(query.toLowerCase())
  );
  return { selected, setSelected, query, setQuery, filtered };
}

function Explore() {
  const { exhibitors } = useData();
  const { selected, setSelected, query, setQuery, filtered } = useProductFilter();
  return <>
    <PageIntro eyebrow="THE DIGITAL EXHIBITION" title={<>Your next <em>connection</em><br/>starts here.</>}><span>Explore a considered selection of the people and products defining fine jewellery today.</span></PageIntro>
    <div className="filter-bar">
      <div className="search-field"><Search size={17}/><input value={query} onChange={e => setQuery(e.target.value)} aria-label="Search the exhibition" placeholder="Search the exhibition" data-testid="exhibition-search-input"/></div>
      <div className="category-pills">
        <button className={selected === "All categories" ? "selected" : ""} onClick={() => setSelected("All categories")} data-testid="category-all-button">All categories</button>
        {categories.map(c => <button className={selected === c ? "selected" : ""} key={c} onClick={() => setSelected(c)} data-testid={`category-${c.toLowerCase().replaceAll(" ", "-")}-button`}>{c}</button>)}
      </div>
    </div>
    <section className="content-section">
      <div className="section-heading">
        <div><div className="eyebrow">CURATED FOR YOU</div><h2>Featured products</h2></div>
        <span className="result-count" data-testid="product-result-count">{filtered.length} results</span>
      </div>
      <div className="product-grid">{filtered.map(p => <ProductCard key={p.id} product={p}/>)}</div>
    </section>
    <section className="content-section directory-preview">
      <div className="section-heading">
        <div><div className="eyebrow">THE PEOPLE BEHIND THE PIECES</div><h2>Discover exhibitors</h2></div>
        <Link className="arrow-link" to="/exhibitors" data-testid="explore-exhibitors-link">View all exhibitors <ArrowUpRight size={16}/></Link>
      </div>
      <div className="exhibitor-grid">{exhibitors.slice(0,3).map(e => <ExhibitorCard key={e.id} exhibitor={e}/>)}</div>
    </section>
  </>;
}

function ProductDirectory() {
  const { selected, setSelected, query, setQuery, filtered } = useProductFilter();
  return <>
    <PageIntro eyebrow="THE PRODUCT DIRECTORY" title={<>Objects of<br/><em>desire.</em></>}><span>A considered edit of diamonds, jewellery and gemstones from the houses exhibiting at CaratJewel.</span></PageIntro>
    <div className="filter-bar">
      <div className="search-field"><Search size={17}/><input value={query} onChange={e => setQuery(e.target.value)} aria-label="Search products or exhibitors" placeholder="Search products or exhibitors" data-testid="product-directory-search-input"/></div>
      <div className="category-pills">
        <button className={selected === "All categories" ? "selected" : ""} onClick={() => setSelected("All categories")} data-testid="product-category-all-button">All products</button>
        {categories.map(c => <button className={selected === c ? "selected" : ""} key={c} onClick={() => setSelected(c)} data-testid={`product-category-${c.toLowerCase().replaceAll(" ", "-")}-button`}>{c}</button>)}
      </div>
    </div>
    <section className="content-section">
      <div className="section-heading">
        <div><div className="eyebrow">ALL PRODUCTS</div><h2>Find your next piece</h2></div>
        <span className="result-count" data-testid="product-directory-result-count">{filtered.length} results</span>
      </div>
      <div className="product-grid">{filtered.map(p => <ProductCard key={p.id} product={p}/>)}</div>
    </section>
  </>;
}

// ---------- Pricing ----------
function Pricing() {
  const {packages,error}=useSite();
  return <PricingPage packages={packages} error={error}/>;
}

// ---------- Exhibition calendar ----------
function ExhibitionStatus({ status, testId }) {
  return <span className={status === "Live" ? "exhibition-status live" : "exhibition-status"} data-testid={testId || `exhibition-status-${status.toLowerCase()}`}><i/>{status}</span>;
}

function ExhibitionCard({ exhibition }) {
  const presentation = showPresentation(exhibition);
  return <article className="exhibition-card" data-testid={`exhibition-card-${exhibition.id}`}>
    <div className="exhibition-card-copy">
      <div className="eyebrow">CARATJEWEL EXPO</div>
      <h2>{presentation.title}</h2>
      <p className="show-category">{exhibition.category}</p>
      <div className="show-card-schedule"><ShowStatus exhibition={exhibition}/><span>{presentation.schedule}</span></div>
      <Link className="button button-dark" to={`/exhibitions/${exhibition.id}`} data-testid={`view-exhibition-${exhibition.id}-link`}>View Show <ArrowUpRight size={15}/></Link>
    </div>
    <SiteImage image={exhibition.featured_image || exhibition.banner_image} alt={exhibition.name}/>
  </article>;
}

function ExhibitionCalendar() {
  const { exhibitions } = useData();
  const next = exhibitions.find(e => liveWindowOpen(e)) || [...exhibitions].filter(e => e.next_open_at).sort((a,b) => a.next_open_at-b.next_open_at)[0] || exhibitions[0];
  return <>
    <PageIntro eyebrow="THE YEAR AHEAD" title={<>Eight moments.<br/><em>One global room.</em></>}><span>CARATJEWEL EXPO is open 365 days a year for discovery — with eight major live B2B exhibitions bringing the industry together.</span></PageIntro>
    {next && <section className="calendar-hero content-section">
      <div className="exhibition-card-copy">
        <div className="eyebrow">CARATJEWEL EXPO</div>
        <h2>{showPresentation(next).title}</h2>
        <p className="show-category">{next.category}</p>
        <div className="show-card-schedule" data-testid="next-exhibition-countdown"><ShowStatus exhibition={next}/><span>{showPresentation(next).schedule}</span></div>
        <Link className="button button-dark" to={`/exhibitions/${next.id}`} data-testid="next-exhibition-view-button">View Show <ArrowUpRight size={16}/></Link>
      </div>
      <SiteImage image={next.featured_image || next.banner_image} alt={next.name}/>
    </section>}
    <section className="calendar-section content-section">
      <div className="section-heading"><div><div className="eyebrow">EXPLORE THE SHOWS</div><h2>Upcoming Exhibitions</h2></div><span data-testid="exhibition-calendar-total">{exhibitions.length} major exhibitions / year</span></div>
      <div className="exhibition-grid-list">{exhibitions.map(e => <ExhibitionCard key={e.id} exhibition={e}/>)}</div>
    </section>
    <section className="calendar-note content-section">
      <div className="eyebrow">A YEAR-ROUND PLATFORM</div>
      <h2>Discover all year.<br/><em>Meet live, eight times.</em></h2>
      <p>Buyers can browse exhibitors and products every day. Each live exhibition creates a focused buying moment for its category, with public broadcasts and private one-to-one conversations.</p>
    </section>
  </>;
}

function ExhibitionNotFound() {
  return <section className="content-section" data-testid="exhibition-not-found">
    <div className="eyebrow">EXHIBITION NOT FOUND</div>
    <h2 style={{ fontSize: "clamp(32px,4vw,55px)", fontWeight: 500, letterSpacing: "-.035em", margin: "12px 0 20px" }}>
      That exhibition<br/><em>isn't in our calendar.</em>
    </h2>
    <p style={{ fontSize: 13, lineHeight: 1.8, color: "#777", maxWidth: 480, margin: "0 0 32px" }}>
      The link may have expired or the edition has moved. Browse the full year to find the next moment for your category.
    </p>
    <Link className="button button-dark" to="/exhibitions" data-testid="back-to-calendar-button">Back to the calendar <ArrowUpRight size={16}/></Link>
  </section>;
}

function ExhibitionLanding() {
  const { id } = useParams();
  const accessKey = useAccessKey();
  return <ExhibitionLandingContent key={`${accessKey}:${id}`}/>;
}

function ExhibitionLandingContent() {
  const { id } = useParams();
  const { exhibitions, exhibitors, loaded, error } = useData();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState("");
  const exhibition = exhibitions.find(item => item.id === id);
  if (!loaded) return <ResourceState title="Loading exhibition…"/>;
  if (!exhibition) return error ? <ResourceState title="Exhibition unavailable"/> : <ExhibitionNotFound/>;
  // Public API returns IDs; admin responses retain legacy arrays for review.
  const featured = [...new Set(exhibition.featured || [])].map((reference) => {
    if (typeof reference !== "string" || !reference.trim()) return null;
    const idMatches = exhibitors.filter((item) => item.id === reference);
    const matches = idMatches.length ? idMatches : exhibitors.filter((item) => item.name.trim().toLowerCase() === reference.trim().toLowerCase());
    return matches.length === 1 && matches[0].status === "approved" ? matches[0] : null;
  }).filter((item, index, all) => item && all.findIndex((other) => other?.id === item.id) === index);
  const target = featured.find((item) => item.id === selectedId);
  const isCompleted = showPresentation(exhibition).status === "Show Completed";
  const onMeeting = async () => {
    if (!target) return;
    if (!user) { navigate("/login"); return; }
    try {
      await api.createMeeting({
        exhibitor_id: target.id,
        exhibition_id: exhibition.id,
        note: `Meeting request from ${exhibition.short} landing page`,
      });
      alert("Meeting request received — our team will confirm a time shortly.");
    } catch (e) { alert(e.message || "Could not send the meeting request. Please try again."); }
  };
  const onInquiry = async () => {
    if (!target) return;
    if (!user) { navigate("/login"); return; }
    try {
      await api.createInquiry({
        buyer_name: user.name,
        buyer_email: user.email,
        exhibitor_id: target.id,
        message: `Inquiry from ${exhibition.short} landing page`,
      });
      alert("Inquiry received — the exhibitor will get back to you.");
    } catch (e) { alert(e.message || "Could not send the inquiry. Please try again."); }
  };
  return <>
    <div className={`exhibition-landing-hero ${liveWindowOpen(exhibition) ? "is-live" : ""}`}>
      <SiteImage image={exhibition.banner_image} alt={exhibition.name}/>
      <div className="exhibition-landing-copy">
        <div className="eyebrow light"><span className="eyebrow-line"/> CARATJEWEL EXPO</div>
        <ShowStatus exhibition={exhibition}/>
        <h1>{showPresentation(exhibition).title}</h1>
        <p>{showPresentation(exhibition).schedule} · {exhibition.category}</p>
        <div className="hero-actions" id="register">
          {isCompleted ? (
            <div className="exhibition-ended-note" data-testid="exhibition-ended-note">
              <strong>This exhibition has ended.</strong>
              <span>The buying room is closed for this edition. Browse featured exhibitors and their collections below, or explore upcoming shows.</span>
              <Link className="arrow-link light" to="/exhibitions" data-testid="exhibition-ended-see-calendar-link">See upcoming exhibitions <ArrowUpRight size={15}/></Link>
            </div>
          ) : (
            <>
              <Link className="button button-light" to={`/login?role=buyer&exhibition=${exhibition.id}`} data-testid="exhibition-register-buyer-button">Register as Buyer <ArrowUpRight size={16}/></Link>
              <Link className="button button-ghost" to={`/exhibit?show=${exhibition.id}`} data-testid="exhibition-exhibit-show-button">Exhibit at this Show</Link>
            </>
          )}
        </div>
      </div>
    </div>
    <section className="exhibition-overview content-section">
      <div>
        <div className="eyebrow">THE BUYING ROOM</div>
        <h2>{liveWindowOpen(exhibition) ? <>The room is<br/><em>open now.</em></> : isCompleted ? <>A moment<br/><em>now archived.</em></> : <>A focused moment<br/><em>for the trade.</em></>}</h2>
        <p>{exhibition.note}</p>
      </div>
      <div className="exhibition-tools">
        <div className="tool-row"><Globe2 size={18}/><div><strong>Public Live Channels</strong><span>Watch exhibitors present to buyers around the world.</span></div></div>
        <div className="tool-row"><ShieldCheck size={18}/><div><strong>Private 1-to-1 Video Rooms</strong><span>Move from discovery to a focused buyer conversation.</span></div></div>
        <div className="tool-row"><CalendarDays size={18}/><div><strong>Request Meeting</strong><span>Set up time with the houses you want to know.</span></div></div>
      </div>
    </section>
    {liveWindowOpen(exhibition) && featured.some(item => item.live && item.live_exhibition_id === exhibition.id) && (
      <section className="live-floor content-section">
        <div className="section-heading">
          <div><div className="eyebrow">ON THE FLOOR NOW</div><h2>Live exhibitor booths</h2></div>
          <span className="live-floor-label"><i/> LIVE NOW</span>
        </div>
        <div className="exhibitor-grid">{featured.filter(item => item.live && item.live_exhibition_id === exhibition.id).map(item => <ExhibitorCard key={item.id} exhibitor={item}/>)}</div>
      </section>
    )}
    <section className="exhibition-discovery content-section">
      <div className="section-heading">
        <div><div className="eyebrow">EXPLORE THE SHOW</div><h2>Discover exhibitors</h2></div>
        <Link className="arrow-link" to="/exhibitors" data-testid="exhibition-exhibitor-directory-link">Exhibitor directory <ArrowUpRight size={16}/></Link>
      </div>
      <div className="exhibitor-grid">{featured.map(item => <ExhibitorCard key={item.id} exhibitor={item}/>)}</div>
      {!isCompleted && (
        <div className="show-actions">
          <label>Choose an exhibitor<select value={target?.id || ""} onChange={(event) => setSelectedId(event.target.value)} data-testid="exhibition-target">
            <option value="">Select an exhibitor</option>
            {featured.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select></label>
          {!featured.length && <p>No featured exhibitors available.</p>}
          <Link className="button button-dark" to="/products" data-testid="exhibition-product-discovery-button">Discover products <ArrowUpRight size={16}/></Link>
          <button className="button button-outline" onClick={onMeeting} disabled={!target} data-testid="exhibition-request-meeting-button">Request Meeting <CalendarDays size={16}/></button>
          <button className="button button-outline" onClick={onInquiry} disabled={!target} data-testid="exhibition-send-inquiry-button">Send Inquiry <ArrowUpRight size={16}/></button>
        </div>
      )}
    </section>
  </>;
}

// ---------- Cards ----------
function ProductCard({ product, exhibitorName }) {
  const { exhibitors } = useData();
  const { user } = useAuth();
  const { isSaved, toggle } = useShortlist();
  const navigate = useNavigate();
  const saved = isSaved("product", product.id);
  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    await toggle("product", product.id);
  };
  if (!product.exhibitor_id) return null;
  const productUrl = `/exhibitors/${encodeURIComponent(product.exhibitor_id)}/products/${encodeURIComponent(product.id)}`;
  const ownerName = exhibitorName || exhibitors.find((item) => item.id === product.exhibitor_id)?.name || "View exhibitor";
  return <article className="product-card" data-testid={`product-card-${product.id}`}>
    <div className={`product-visual ${product.accent} ${product.image ? "has-image" : "photo-ready"}`} style={product.image ? { backgroundImage: `url(${product.image})` } : undefined}>
      <span>{product.type}</span>
      <button className={saved ? "save-button saved" : "save-button"} onClick={handleToggle} aria-label={saved ? "Remove from shortlist" : "Save product"} data-testid={`save-product-${product.id}-button`}>
        <Heart size={17} fill={saved ? "currentColor" : "none"}/>
      </button>
      {!product.image && <div className="product-shape"/>}
    </div>
    <Link className="product-meta" to={productUrl} data-testid={`product-open-${product.id}`}>
      <div><h3>{product.name}</h3><p>{ownerName}</p></div>
      <ArrowUpRight size={17}/>
    </Link>
    <small>{product.detail}</small>
  </article>;
}

function ExhibitorCard({ exhibitor }) {
  const { user } = useAuth();
  const { isSaved, toggle } = useShortlist();
  const navigate = useNavigate();
  const saved = isSaved("exhibitor", exhibitor.id);
  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    await toggle("exhibitor", exhibitor.id);
  };
  return <Link className="exhibitor-card" to={`/exhibitors/${exhibitor.id}`} data-testid={`exhibitor-card-${exhibitor.id}`}>
    <div className={`exhibitor-art ${exhibitor.accent}`}>
      <span>{exhibitor.initials}</span>
      <div className="art-orbit"/>
      <span className={exhibitor.live ? "status-chip live" : "status-chip"}><i/>{exhibitor.live ? "LIVE" : "OFFLINE"}</span>
      <button className={saved ? "save-button exhibitor-save saved" : "save-button exhibitor-save"} onClick={handleToggle} aria-label={saved ? "Remove exhibitor from shortlist" : "Save exhibitor"} data-testid={`save-exhibitor-${exhibitor.id}-button`}>
        <Heart size={16} fill={saved ? "currentColor" : "none"}/>
      </button>
    </div>
    <div className="exhibitor-meta">
      <div>
        <h3>{exhibitor.name}{exhibitor.verified && <span className="verified-badge" title="Verified exhibitor" data-testid={`verified-${exhibitor.id}-badge`}><ShieldCheck size={12}/></span>}</h3>
        <p>{exhibitor.city}, {exhibitor.country}</p>
      </div>
      <ArrowUpRight size={17}/>
    </div>
    <small>{exhibitor.specialty}</small>
  </Link>;
}

// ---------- Exhibitor directory + booth ----------
function Exhibitors() {
  const { exhibitors } = useData();
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("All locations");
  const locations = ["All locations", ...new Set(exhibitors.map(e => e.country))];
  const filtered = exhibitors.filter(e =>
    (location === "All locations" || e.country === location) &&
    `${e.name} ${e.city} ${e.country} ${e.specialty}`.toLowerCase().includes(query.toLowerCase())
  );
  return <>
    <PageIntro eyebrow="THE EXHIBITOR DIRECTORY" title={<>Meet the <em>makers.</em></>}><span>Featured specialist houses, ateliers and manufacturers — ready to do business.</span></PageIntro>
    <div className="directory-toolbar">
      <div className="search-field"><Search size={17}/><input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by company, city or specialty" data-testid="exhibitor-search-input"/></div>
      <label className="filter-select" style={{ position: "relative" }}>{location}<ChevronDown size={16}/>
        <select value={location} onChange={e => setLocation(e.target.value)} aria-label="Filter exhibitors by location" data-testid="exhibitor-filter-select" style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}>
          {locations.map(item => <option key={item}>{item}</option>)}
        </select>
      </label>
    </div>
    <section className="content-section">
      <div className="section-heading">
        <div className="eyebrow">FEATURED EXHIBITORS</div>
        <span className="result-count" data-testid="exhibitor-result-count">{filtered.length} shown</span>
      </div>
      <div className="exhibitor-grid full">{filtered.map(e => <ExhibitorCard key={e.id} exhibitor={e}/>)}</div>
    </section>
  </>;
}

function ResourceState({ title }) {
  return <section className="content-section" role="status"><h1>{title}</h1><Link to="/exhibitors">Browse exhibitors</Link></section>;
}

function Booth() {
  const { id } = useParams();
  const accessKey = useAccessKey();
  return <BoothContent key={`${id}:${accessKey}`} id={id}/>;
}

function BoothContent({ id }) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [exhibitor, setExhibitor] = useState(null);
  const [displayed, setDisplayed] = useState([]);
  const [error, setError] = useState("");
  const [watching, setWatching] = useState(false);
  const isOwner = user?.role === "exhibitor" && user.exhibitor_id === exhibitor?.id;
  useEffect(() => {
    if (!exhibitor?.live || !exhibitor.live_deadline) return;
    const timer = setTimeout(() => setExhibitor(previous => previous?.id === id ? {...previous, live:false} : previous), Math.max(0, exhibitor.live_deadline-Date.now()));
    return () => clearTimeout(timer);
  }, [exhibitor?.live, exhibitor?.live_deadline, id]);
  useEffect(() => {
    if (authLoading) return;
    const controller = new AbortController();
    let timer;
    const refresh = async () => {
      try {
        const [booth, catalogue] = await Promise.all([
          api.getExhibitor(id, { signal: controller.signal }),
          api.listProducts({ exhibitor_id: id }, { signal: controller.signal }),
        ]);
        if (controller.signal.aborted) return;
        if (booth.id !== id || !Array.isArray(catalogue)) throw new Error("Invalid booth response");
        setExhibitor({...booth, live_deadline: liveDeadline(booth)});
        setDisplayed(catalogue.filter(product => product.exhibitor_id === id));
        setError("");
      } catch (err) {
        if (controller.signal.aborted) return;
        setExhibitor(null); setDisplayed([]); setWatching(false);
        setError(err.status === 404 ? "Booth Not Found" : "Booth unavailable. Please try again.");
      }
      if (!controller.signal.aborted) timer = setTimeout(refresh, 8000);
    };
    refresh();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [id, authLoading]);
  if (!exhibitor) return <ResourceState title={error || "Loading booth…"}/>;
  const requesterFields = () => (
    user ? { requester_name: user.name, requester_email: user.email } : null
  );
  const inquirerFields = () => (
    user ? { buyer_name: user.name, buyer_email: user.email } : null
  );
  const sendInquiry = async () => {
    const fields = inquirerFields();
    if (!fields) { navigate("/login"); return; }
    try {
      await api.createInquiry({ ...fields, exhibitor_id: exhibitor.id, message: `Interested in ${exhibitor.name}'s collection.` });
      alert("Inquiry sent — the exhibitor will be in touch.");
    } catch { alert("Could not send the inquiry. Please try again."); }
  };
  const requestMeeting = async () => {
    const fields = requesterFields();
    if (!fields) { navigate("/login"); return; }
    try {
      await api.createMeeting({ ...fields, exhibitor_id: exhibitor.id });
      alert("Meeting requested — we'll confirm a time shortly.");
    } catch (err) {
      alert(err.message || "Could not request the meeting. Please try again.");
    }
  };
  const openWatchLive = () => {
    if (!user) { navigate("/login"); return; }
    setWatching(true);
  };
  return <>
    <div className={`booth-cover ${exhibitor.accent}`}>
      <div className="booth-cover-inner">
        <div className="eyebrow light">EXHIBITOR BOOTH{exhibitor.booth_code ? ` · ${exhibitor.booth_code}` : ""}</div>
        <div className="booth-identity">
          <span className="booth-logo">{exhibitor.initials}</span>
          <div>
            <h1>{exhibitor.name} {exhibitor.verified && <span className="verified-badge light-badge" data-testid="booth-verified-badge"><ShieldCheck size={15}/></span>}</h1>
            <p>{exhibitor.country} · {exhibitor.specialty}</p>
          </div>
        </div>
        <div className={exhibitor.live ? "booth-live live" : "booth-live"} data-testid={`booth-live-status-${exhibitor.live ? "live" : "offline"}`}><i/>{exhibitor.live ? "LIVE NOW" : "OFFLINE"}</div>
      </div>
    </div>
    <section className="booth-content">
      <div className="booth-about">
        <div className="eyebrow">ABOUT THE HOUSE</div>
        <h2>Crafted with<br/><em>quiet conviction.</em></h2>
        <p>{exhibitor.name} brings a considered approach to {exhibitor.specialty.toLowerCase()}, combining deep expertise with a forward-looking point of view.</p>
        <div className="booth-specialties"><span>{exhibitor.specialty}</span><span>{exhibitor.city}</span><span>Verified house</span></div>
        <div className="booth-actions">
          {!isOwner && <ChatButton exhibitorId={exhibitor.id}/>}
          {exhibitor.live && !isOwner && !authLoading && (
            <button className="button button-dark" onClick={openWatchLive} data-testid="watch-live-button">
              <Video size={16}/>&nbsp;Watch Live
            </button>
          )}
          {exhibitor.live && isOwner && (
            <Link className="button button-dark" to="/dashboard/exhibitor" data-testid="return-to-workspace-button">
              Return to workspace <ArrowUpRight size={16}/>
            </Link>
          )}
          {!isOwner && !authLoading && (
            <>
              <button className="button button-dark" onClick={sendInquiry} data-testid="booth-inquiry-button">Send Inquiry <ArrowUpRight size={16}/></button>
              <button className="button button-outline" onClick={requestMeeting} data-testid="booth-meeting-button">Request Meeting <CalendarDays size={16}/></button>
            </>
          )}
        </div>
      </div>
      <div className="booth-products" id="collection">
        <div className="section-heading"><h2>Product catalogue</h2><span>Selected collection</span></div>
        {displayed.length ? <div className="product-grid mini">{displayed.map(p => <ProductCard key={p.id} product={p} exhibitorName={exhibitor.name}/>)}</div> : <p>No products</p>}
      </div>
    </section>
    {watching && <WatchLiveModal exhibitor={exhibitor} onClose={() => setWatching(false)}/>}
  </>;
}

function ProductDetail() {
  const { exhibitorId, productId } = useParams();
  const accessKey = useAccessKey();
  return <ProductDetailContent key={`${exhibitorId}:${productId}:${accessKey}`} exhibitorId={exhibitorId} productId={productId}/>;
}

function ProductDetailContent({ exhibitorId, productId }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (loading) return;
    const controller = new AbortController();
    (async () => {
      try {
        const product = await api.getProduct(productId, exhibitorId, { signal: controller.signal });
        if (product.id !== productId || !product.exhibitor_id || (exhibitorId && product.exhibitor_id !== exhibitorId)) throw Object.assign(new Error(), {status: 404});
        const booth = await api.getExhibitor(product.exhibitor_id, { signal: controller.signal });
        if (booth.id !== product.exhibitor_id) throw Object.assign(new Error(), {status: 404});
        if (!controller.signal.aborted) setData({ product, booth });
      } catch (err) {
        if (!controller.signal.aborted) setError(err.status === 404 ? "Product Not Found" : "Product unavailable. Please try again.");
      }
    })();
    return () => controller.abort();
  }, [exhibitorId, productId, loading]);
  if (!data) return <ResourceState title={error || "Loading product…"}/>;
  const { product, booth } = data;
  const sendInquiry = async () => {
    if (!user) { navigate("/login"); return; }
    if (sending) return;
    setSending(true); setNotice("");
    try {
      await api.createInquiry({ exhibitor_id: booth.id, product_id: product.id, buyer_name: user.name, buyer_email: user.email, message: `Interested in ${product.name}.` });
      setNotice("Inquiry sent — the exhibitor will be in touch.");
    } catch (err) { setNotice(err.message || "Could not send the inquiry. Please try again."); }
    finally { setSending(false); }
  };
  return <section className="content-section">
    <Link to={`/exhibitors/${encodeURIComponent(booth.id)}`}>Back to {booth.name}</Link>
    <h1>{product.name}</h1>
    <ProductCard product={product} exhibitorName={booth.name}/>
    <p>{product.detail}</p>
    <ChatButton exhibitorId={booth.id} productId={product.id}/>
    <button className="button button-dark" data-testid="product-inquiry-button" disabled={sending} onClick={sendInquiry}>{sending ? "Sending…" : "Send Inquiry"}</button>
    {notice && <p role="status">{notice}</p>}
  </section>;
}

// ---------- Exhibit With Us ----------
function Exhibit() {
  const location = useLocation();
  const navigate = useNavigate();
  const {exhibitions, loaded} = useData();
  const search = new URLSearchParams(location.search);
  const initialPlan = search.get("plan") || "professional";
  const selectedShowId = search.get("show") || "";
  const selectedPlan = initialPlan;
  const [application, setApplication] = useState({company:"", email:"", phone:"", country:"", address:"", employeeCount:"", website:"", socialUrl:"", description:""});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [proofFile, setProofFile] = useState(null);
  const [proofStatus, setProofStatus] = useState("empty");
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const {packages: pricingPlans, error: packageError} = useSite();
  const applicationShows = exhibitorApplicationShows(exhibitions);
  const selectedShow = applicationShows.find(show => show.id === selectedShowId);
  const applicationOpen = search.get("step") === "application" && !!selectedShow;
  const selected = pricingPlans.find(plan => plan.id === selectedPlan && plan.active !== false && plan.selectable !== false);
  const updateStep = (showId, step) => {
    const next = new URLSearchParams(location.search);
    next.set("show", showId);
    if (step) next.set("step", step); else next.delete("step");
    navigate({pathname: "/exhibit", search: `?${next.toString()}`}, {replace: true});
  };
  const updateField = (field, value) => {
    setApplication(current => ({...current, [field]: value}));
    setErrors(current => ({...current, [field]: ""}));
    setNotice("");
  };
  const toggleCategory = (value) => {
    setSelectedCategories(current => current.includes(value) ? current.filter(item => item !== value) : [...current, value]);
    setErrors(current => ({...current, categories: ""}));
    setNotice("");
  };
  const validateApplication = () => {
    const next = {};
    if (!application.company.trim()) next.company = "Enter your company name.";
    if (!/^\S+@\S+\.\S+$/.test(application.email.trim())) next.email = "Enter a valid business email.";
    const phoneDigits = application.phone.replace(/\D/g, "");
    if (!/^\+?[0-9 ()-]+$/.test(application.phone.trim()) || phoneDigits.length < 7 || phoneDigits.length > 15) next.phone = "Enter a valid phone or WhatsApp number.";
    if (!application.country.trim()) next.country = "Enter your country.";
    if (!selectedCategories.length) next.categories = "Choose at least one category.";
    if (!application.description.trim()) next.description = "Add a short company description.";
    for (const field of ["website", "socialUrl"]) {
      if (application[field]) {
        try { const parsed = new URL(application[field]); if (!/^https?:$/.test(parsed.protocol)) throw new Error(); }
        catch { next[field] = "Enter a complete http(s) URL."; }
      }
    }
    return next;
  };
  const chooseProof = (event) => {
    const file = event.target.files?.[0] || null;
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (file && (!allowed.includes(file.type) || file.size > 8 * 1024 * 1024)) {
      setProofFile(null); setProofStatus("invalid");
      setErrors(current => ({...current, proof: "Use a PDF, JPEG, PNG or WebP file up to 8 MB."}));
      event.target.value = "";
      return;
    }
    setProofFile(file); setProofStatus(file ? "selected" : "empty");
    setErrors(current => ({...current, proof: ""}));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !selected || !selectedShow) return;
    const validationErrors = validateApplication();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setNotice("Please review the highlighted fields.");
      return;
    }
    setSubmitting(true);
    setNotice("");
    try {
      const created = await api.createApplication({
        company: application.company.trim(), email: application.email.trim(), phone: application.phone.trim(),
        country: application.country.trim(), address: application.address.trim(), employee_count: application.employeeCount || null,
        website: application.website.trim() || null, social_url: application.socialUrl.trim() || null,
        description: application.description.trim(), categories: selectedCategories,
        category: selectedCategories.join(", "), plan: selectedPlan, show_id: selectedShow.id,
      });
      if (proofFile) {
        setProofStatus("uploading");
        try {
          await api.uploadApplicationProof(created.id, created.proof_upload_token, proofFile);
          setProofStatus("uploaded");
          setNotice("Application and company proof received. Our team will review your submission.");
        } catch {
          setProofStatus("failed");
          setNotice("Your application was received, but the proof document could not be uploaded. Our team can request it during review.");
        }
      } else {
        setNotice("Application received. Our team will review your submission.");
      }
    } catch (err) { setNotice("Could not submit the application. Please review your details and try again."); }
    finally { setSubmitting(false); }
  };
  return <>
    <PageIntro eyebrow={applicationOpen ? "EXHIBITOR APPLICATION" : "CHOOSE EXHIBITION"} title={applicationOpen ? <>Apply to<br/><em>exhibit.</em></> : <>Choose your<br/><em>show.</em></>}><span>{applicationOpen ? "Complete your exhibitor application for the selected CARATJEWEL EXPO show." : "Select an upcoming exhibition, review its focus and continue when it is right for your business."}</span></PageIntro>
    {!applicationOpen&&<section className="exhibit-show-selection" data-testid="exhibition-selection-step">
      <div className="exhibit-show-heading"><div><div className="eyebrow">01 · CHOOSE EXHIBITION</div><h2>Where would you like to exhibit?</h2></div><p>Choose a show to view its focus and exhibitor profile. Selecting a show does not submit an application.</p></div>
      {!loaded?<div className="exhibit-show-loading" role="status">Loading upcoming shows…</div>:applicationShows.length?<div className="exhibit-show-track" aria-label="Upcoming exhibitions">{applicationShows.map(show=><button type="button" key={show.key} className={`exhibit-show-card${selectedShowId===show.id?' selected':''}`} aria-pressed={selectedShowId===show.id} onClick={()=>updateStep(show.id, '')} data-testid={`exhibition-option-${show.key}`}>
        <span className="exhibit-show-card-top"><span className="exhibit-show-brand">{show.brand}</span><span className="exhibit-show-status">{show.status}</span></span>
        <h3>{show.name}</h3>
        <span className="exhibit-show-card-footer"><strong>{show.category}</strong><span>{show.schedule}</span></span>
        {selectedShowId===show.id&&<span className="exhibit-show-selected">Selected</span>}
      </button>)}</div>:<div className="exhibit-show-loading" role="status">Upcoming exhibition information is temporarily unavailable.</div>}
      {selectedShow&&<article className="exhibit-show-details" data-testid="selected-exhibition-details">
        <div><div className="eyebrow">02 · VIEW SHOW DETAILS</div><h3>{selectedShow.brand} — {selectedShow.name}</h3><p>{selectedShow.description}</p></div>
        <dl className="exhibit-show-facts"><div><dt>Category</dt><dd>{selectedShow.category}</dd></div><div><dt>Schedule</dt><dd className="show-schedule"><span className="show-status">{selectedShow.status}</span><br/>{selectedShow.schedule}</dd></div><div><dt>Who should exhibit</dt><dd>{selectedShow.exhibitors}</dd></div></dl>
        <div className="exhibit-show-actions"><span>Your selection will be carried into the application.</span><button type="button" className="button button-dark" onClick={()=>updateStep(selectedShow.id, 'application')} data-testid="continue-exhibitor-application">Continue to Exhibitor Application <ArrowUpRight size={16}/></button></div>
      </article>}
    </section>}
    {applicationOpen&&<><section className="apply-pricing-reference">
      <div><div className="eyebrow">ONE PACKAGE GUIDE</div><h2>Review packages on the pricing page.</h2><p>Launch prices, regular prices, feature details and availability are maintained in one place.</p></div>
      <Link className="arrow-link" to="/pricing" data-testid="exhibit-view-pricing-link">Review launch pricing <ArrowUpRight size={16}/></Link>
    </section>
    <section className="apply-layout">
      <div className="apply-copy">
        <div className="numbered"><span>01</span><div><h3>Be discovered</h3><p>Place your brand and collection in front of buyers exploring jewellery suppliers.</p></div></div>
        <div className="numbered"><span>02</span><div><h3>Build relationships</h3><p>Turn digital introductions into conversations, meetings and long-term partnerships.</p></div></div>
        <div className="numbered"><span>03</span><div><h3>Grow with intention</h3><p>Understand your audience and create a presence that works as hard as you do.</p></div></div>
      </div>
      <form className="apply-form application-form-shell" onSubmit={submit} noValidate data-testid="exhibitor-application-form">
        <div className="eyebrow">EXHIBITOR APPLICATION</div>
        <h2>Tell us about<br/><em>your house.</em></h2>
        <div className="application-show-note" data-testid="selected-exhibition-note"><span>Selected Exhibition</span><strong>{selectedShow.brand} — {selectedShow.name}</strong><button type="button" onClick={()=>updateStep(selectedShow.id, '')}>Change exhibition</button></div>
        <div className="selected-plan-note" data-testid="selected-plan-note"><span>Selected package</span><strong>{selected ? `${selected.name} · Application subject to review` : (packageError || "Select an available package on the pricing page")}</strong></div>
        <section className="application-form-section" aria-labelledby="company-information-title">
          <div className="application-section-heading"><span>01</span><div><h3 id="company-information-title">Company Information</h3><p>Tell us who you are and where your business operates.</p></div></div>
          <div className="application-field-grid">
            <label>Company Name <b aria-hidden="true">*</b><input required value={application.company} onChange={e => updateField("company", e.target.value)} data-testid="application-company-input" aria-invalid={!!errors.company} placeholder="e.g. Aurum Atelier"/>{errors.company&&<small className="application-field-error">{errors.company}</small>}</label>
            <label>Country <b aria-hidden="true">*</b><input required value={application.country} onChange={e => updateField("country", e.target.value)} data-testid="application-country-input" aria-invalid={!!errors.country} placeholder="e.g. India"/>{errors.country&&<small className="application-field-error">{errors.country}</small>}</label>
            <label className="application-field-wide">Company Address <i>Optional</i><textarea value={application.address} onChange={e => updateField("address", e.target.value)} data-testid="application-address-input" placeholder="Registered or principal business address" rows="3"/></label>
            <label>Employee Count<select value={application.employeeCount} onChange={e => updateField("employeeCount", e.target.value)} data-testid="application-employee-count">
              <option value="">Select range (optional)</option><option>1–10</option><option>11–50</option><option>51–200</option><option>201–500</option><option>501+</option>
            </select></label>
          </div>
        </section>
        <section className="application-form-section" aria-labelledby="business-profile-title">
          <div className="application-section-heading"><span>02</span><div><h3 id="business-profile-title">Business Profile</h3><p>Help our review team understand your company and market presence.</p></div></div>
          <div className="application-field-grid">
            <label>Website <i>Optional</i><input type="url" value={application.website} onChange={e => updateField("website", e.target.value)} data-testid="application-website-input" aria-invalid={!!errors.website} placeholder="https://company.com"/>{errors.website&&<small className="application-field-error">{errors.website}</small>}</label>
            <label>Instagram / LinkedIn / Company Page <i>Optional</i><input type="url" value={application.socialUrl} onChange={e => updateField("socialUrl", e.target.value)} data-testid="application-social-input" aria-invalid={!!errors.socialUrl} placeholder="https://linkedin.com/company/..."/>{errors.socialUrl&&<small className="application-field-error">{errors.socialUrl}</small>}</label>
            <label className="application-field-wide">Short Company Description <b aria-hidden="true">*</b><textarea required value={application.description} onChange={e => updateField("description", e.target.value)} data-testid="application-description-input" aria-invalid={!!errors.description} placeholder="Describe your company, collections and the buyers you serve." rows="5"/>{errors.description&&<small className="application-field-error">{errors.description}</small>}</label>
          </div>
        </section>
        <section className="application-form-section" aria-labelledby="application-categories-title">
          <div className="application-section-heading"><span>03</span><div><h3 id="application-categories-title">What do you represent?</h3><p>Select every category that applies to your business.</p></div></div>
          <fieldset className="application-category-grid" data-testid="application-category-multiselect" aria-invalid={!!errors.categories}>
            <legend className="sr-only">Categories</legend>
            {applicationCategories.map(value=><label key={value} className={selectedCategories.includes(value)?"selected":""}><input type="checkbox" checked={selectedCategories.includes(value)} onChange={()=>toggleCategory(value)}/><span>{value}</span></label>)}
          </fieldset>
          {errors.categories&&<small className="application-field-error">{errors.categories}</small>}
        </section>
        <section className="application-form-section" aria-labelledby="verification-proof-title">
          <div className="application-section-heading"><span>04</span><div><h3 id="verification-proof-title">Verification / Proof</h3><p>An optional company document helps our team review your business.</p></div></div>
          <label className="application-proof-control">Company proof <i>Optional</i><input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" onChange={chooseProof} data-testid="application-proof-input"/><span>Company registration, trade licence, GST or equivalent proof, or a company profile. PDF or image, up to 8 MB.</span></label>
          <div className={`application-proof-status ${proofStatus}`} role="status" data-testid="application-proof-status">{proofStatus==="selected"&&<>Selected: <strong>{proofFile?.name}</strong> · Ready to upload with application</>}{proofStatus==="uploading"&&<>Uploading <strong>{proofFile?.name}</strong>…</>}{proofStatus==="uploaded"&&<>Uploaded for admin review: <strong>{proofFile?.name}</strong></>}{proofStatus==="failed"&&<>Application saved · proof upload needs follow-up</>}{(proofStatus==="empty"||proofStatus==="invalid")&&<>No proof document selected</>}</div>
          {errors.proof&&<small className="application-field-error">{errors.proof}</small>}
        </section>
        <section className="application-form-section" aria-labelledby="contact-details-title">
          <div className="application-section-heading"><span>05</span><div><h3 id="contact-details-title">Contact Details</h3><p>Use the contact details our exhibition team should reply to.</p></div></div>
          <div className="application-field-grid">
            <label>Business Email <b aria-hidden="true">*</b><input required type="email" value={application.email} onChange={e => updateField("email", e.target.value)} data-testid="application-email-input" aria-invalid={!!errors.email} placeholder="you@company.com"/>{errors.email&&<small className="application-field-error">{errors.email}</small>}</label>
            <label>Phone / WhatsApp <b aria-hidden="true">*</b><input required type="tel" value={application.phone} onChange={e => updateField("phone", e.target.value)} data-testid="application-phone-input" aria-invalid={!!errors.phone} placeholder="+91 98765 43210"/>{errors.phone&&<small className="application-field-error">{errors.phone}</small>}</label>
          </div>
        </section>
        {notice&&<p className="application-form-notice" role={notice.startsWith("Could")||notice.startsWith("Please")?"alert":"status"} data-testid="application-form-notice">{notice}</p>}
        <button className="button button-dark" type="submit" disabled={submitting || !selected || !selectedShow} data-testid="application-submit-button">
          {submitting ? "Submitting…" : "Submit exhibitor application"} <ArrowUpRight size={16}/>
        </button>
      </form>
    </section></>}
  </>;
}

// ---------- Auth ----------
function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;
    navigate(
      user.role === "admin" ? "/admin"
      : user.role === "exhibitor" ? "/dashboard/exhibitor"
      : "/dashboard/buyer",
      { replace: true }
    );
  }, [user, navigate]);
  return <div className="auth-page">
    <div className="auth-aside" >
      <Link className="brand light-brand" to="/" data-testid="auth-brand-link"><span className="brand-mark"><Diamond size={17}/></span>CARATJEWEL <b>EXPO</b></Link>
      <div><div className="eyebrow light">YOUR PRIVATE VIEWING ROOM</div><h1>Welcome to<br/><em>the expo.</em></h1></div>
    </div>
    <div className="auth-form">
      <div className="eyebrow">SIGN IN</div>
      <h2 style={{ marginTop: 12 }}>Continue with Google</h2>
      <p>One account for buyers, exhibitors and admins. Your workspace opens automatically after sign-in.</p>
      <button className="button button-dark full-button google-btn" onClick={startGoogleSignIn} data-testid="google-signin-button">
        <span className="google-g" aria-hidden="true">G</span>
        Continue with Google
        <ArrowUpRight size={16}/>
      </button>
      <small className="auth-note"><ShieldCheck size={14}/> Managed by Emergent · your session is encrypted and expires in 7 days</small>
      <div className="auth-role-hints">
        <span><b>Buyer</b> · browse the exhibition, shortlist and inquire</span>
        <span><b>Exhibitor</b> · granted after your application is approved</span>
        <span><b>Admin</b> · granted to allow-listed emails</span>
      </div>
    </div>
    <style>{`
      .google-btn{gap:14px}
      .google-g{display:inline-grid;place-items:center;width:22px;height:22px;border-radius:50%;background:#fff;color:#1a1a1a;font:700 12px 'Manrope',sans-serif;letter-spacing:0}
      .role-tag{font:9px 'DM Mono',monospace;letter-spacing:.08em;text-transform:uppercase;color:#638d85}
      .auth-role-hints{display:grid;gap:8px;border-top:1px solid var(--line);padding-top:22px;margin-top:32px}
      .auth-role-hints span{font-size:11px;color:#777;line-height:1.6}
      .auth-role-hints b{color:var(--ink);font-weight:600;margin-right:6px}
    `}</style>
  </div>;
}

function BuyerDashboard({ displayName }) {
  const { user } = useAuth();
  const {packages: pricingPlans} = useSite();
  const { items, loading, toggle } = useShortlist();
  const { exhibitors, exhibitions } = useData();
  const savedProducts = items.filter((r) => r.kind === "product" && r.product);
  const savedExhibitors = items.filter((r) => r.kind === "exhibitor" && r.exhibitor);
  const [meetings, setMeetings] = useState(null);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetingError, setMeetingError] = useState("");
  const [exhibitorApplications, setExhibitorApplications] = useState(null);
  const now = useNow();
  const reloadMeetings = useCallback(async () => {
    try { setMeetings(await api.buyerMeetings()); setMeetingError(""); }
    catch { setMeetingError("Meetings could not be refreshed. Please try again."); }
  }, []);
  useEffect(() => { reloadMeetings(); }, [reloadMeetings]);
  useEffect(() => {
    let active = true;
    api.myExhibitorApplications().then(rows => { if (active) setExhibitorApplications(rows); }).catch(() => { if (active) setExhibitorApplications([]); });
    return () => { active = false; };
  }, []);
  useEffect(() => { const timer = setInterval(reloadMeetings, 15000); window.addEventListener("cjx-meeting-changed", reloadMeetings); return () => {clearInterval(timer);window.removeEventListener("cjx-meeting-changed", reloadMeetings);}; }, [reloadMeetings]);
  const buyerDecision = async (m, status) => {
    try { await api.updateMeeting(m.id, {status, revision: m.revision}); window.dispatchEvent(new Event("cjx-meeting-changed")); if (status !== "confirmed") setActiveMeeting(null); await reloadMeetings(); }
    catch (e) { alert(e.message || "Could not update the meeting."); await reloadMeetings(); }
  };
  const buyerCancel = async (m) => {
    if (!window.confirm("Cancel this meeting? The exhibitor will be notified.")) return;
    try { await api.updateMeeting(m.id, { status: "cancelled", revision: m.revision }); window.dispatchEvent(new Event("cjx-meeting-changed")); setActiveMeeting(null); reloadMeetings(); }
    catch (e) { alert(e.message || "Could not cancel the meeting."); }
  };
  const buyerReschedule = async (m) => {
    const current = toDatetimeLocal(m.confirmed_time || m.requested_time || new Date().toISOString());
    const next = window.prompt(
      "Propose a new meeting time (leave blank to keep current).\n\nFormat: YYYY-MM-DDTHH:MM",
      current
    );
    if (!next) return;
    try {
      const iso = new Date(next).toISOString();
      await api.rescheduleMeeting(m.id, { proposed_time: iso, revision: m.revision }); window.dispatchEvent(new Event("cjx-meeting-changed"));
      setActiveMeeting(null);
      reloadMeetings();
    } catch (e) { alert(e.message || "Could not propose a new time."); }
  };
  const exhibitorName = (id) => exhibitors.find((e) => e.id === id)?.name || "the exhibitor";
  const vstatus = user?.verification_status || "not_applied";
  const latestExhibitorApplication = exhibitorApplications?.[0] || null;
  const selectedApplicationPackage = latestExhibitorApplication
    ? pricingPlans.find(item => item.id === latestExhibitorApplication.plan)?.name || latestExhibitorApplication.plan || "Not selected"
    : "";
  const applicationStatusCopy = latestExhibitorApplication ? {
    pending: {
      heading: "Application under review",
      body: "Payment unlocks only after our admin team verifies and approves your company.",
    },
    rejected: {
      heading: "Application not approved",
      body: "Payment remains unavailable for this application. Contact our team before submitting updated company information.",
    },
    approved: {
      heading: "Company approved",
      body: "Your approved package is payment-eligible. Refresh account access to continue from the exhibitor workspace.",
    },
  }[latestExhibitorApplication.status] : null;
  const cardBody = {
    not_applied: { title: "Get your Verified Buyer badge", body: "Apply for CARATJEW Verified Buyer status — a premium badge shown to exhibitors across meetings, inquiries and live booths.", cta: "Apply for Verification", to: "/verify" },
    pending: { title: "You're on our review list", body: "Your application is being reviewed by our admin team. We'll email you as soon as there's a decision.", cta: "View application", to: "/verify" },
    more_info: { title: "A few more details needed", body: "Our admin team needs a few more details before we can verify you. Please update and resubmit your application.", cta: "Update Application", to: "/verify" },
    verified: { title: "Your badge is active", body: "You are a verified buyer. Your badge is shown wherever you engage with exhibitors — inquiries, meetings, live booths.", cta: "View badge details", to: "/verify" },
    rejected: { title: "Reapply anytime", body: "Your previous application wasn't approved. You may update and resubmit at any time.", cta: "Resubmit application", to: "/verify" },
  }[vstatus];
  return <>
    <div className="dashboard-head">
      <div>
        <div className="eyebrow">BUYER WORKSPACE</div>
        <h1>Good morning, <em data-testid="dashboard-user-name">{displayName}.</em>{vstatus === "verified" && <>&nbsp;<VerifiedBadge size={13} testId="dashboard-verified-badge"/></>}</h1>
      </div>
      <Link className="button button-dark" to="/explore" data-testid="dashboard-primary-button">Explore exhibition <ArrowUpRight size={16}/></Link>
    </div>
    <section className={`verify-card verify-card-${vstatus}`} data-testid={`verification-card-${vstatus}`}>
      <div className="verify-card-body">
        <VerificationPill status={vstatus} testId="verification-card-pill"/>
        <h3>{cardBody.title}</h3>
        <p>{cardBody.body}</p>
      </div>
      <Link className="button button-outline" to={cardBody.to} data-testid="verification-card-cta">
        {cardBody.cta} <ArrowUpRight size={14}/>
      </Link>
    </section>
    {latestExhibitorApplication&&applicationStatusCopy&&<section className={`application-account-card status-${latestExhibitorApplication.status}`} data-testid={`application-payment-gate-${latestExhibitorApplication.status}`}>
      <div><div className="eyebrow">EXHIBITOR APPLICATION</div><span className="application-account-status">{latestExhibitorApplication.status}</span><h2>{applicationStatusCopy.heading}</h2><p>{applicationStatusCopy.body}</p></div>
      <dl><div><dt>Company</dt><dd>{latestExhibitorApplication.company}</dd></div><div><dt>Selected exhibition</dt><dd>{exhibitorApplicationShowLabel(latestExhibitorApplication.show_id, exhibitions)}</dd></div><div><dt>Selected package</dt><dd>{selectedApplicationPackage}</dd></div><div><dt>Payment</dt><dd>{latestExhibitorApplication.status === "approved" ? "Available after account refresh" : "Unavailable until approval"}</dd></div></dl>
      {latestExhibitorApplication.status === "approved"&&<button className="button button-outline" type="button" onClick={()=>window.location.reload()} data-testid="refresh-approved-exhibitor-access">Refresh account access</button>}
    </section>}
    <section className="dashboard-grid buyer-shortlist-layout">
      <div className="dash-main">
        <div className="dash-title">
          <h2>Your shortlist</h2>
          <span data-testid="shortlist-total-count">
            {loading && items.length === 0 ? "loading…" : `${items.length} saved`}
          </span>
        </div>

        <div className="shortlist-section" data-testid="saved-products-section">
          <div className="shortlist-section-head">
            <div className="eyebrow">SAVED PRODUCTS</div>
            <span data-testid="saved-products-count"><b>{savedProducts.length}</b> item{savedProducts.length === 1 ? "" : "s"}</span>
          </div>
          {savedProducts.length === 0 ? (
            <div className="shortlist-empty" data-testid="saved-products-empty">
              You haven't saved any products yet. Tap the <span className="inline-heart"><Heart size={12}/></span> on any product card to add it here.
            </div>
          ) : (
            <div className="shortlist-list">
              {savedProducts.map((row) => (
                <div className="shortlist-row" key={row.id} data-testid={`shortlist-product-${row.item_id}`}>
                  <div className={`tiny-visual ${row.product.accent || "diamond"}`}/>
                  <div>
                    <h3>{row.product.name}</h3>
                    <p>{row.product.exhibitor} · {row.product.detail}</p>
                  </div>
                  <div className="shortlist-row-actions">
                    <button className="product-btn" onClick={() => toggle("product", row.item_id)} data-testid={`shortlist-remove-product-${row.item_id}`}>
                      Remove
                    </button>
                    <Link className="product-btn" to={`/exhibitors/${encodeURIComponent(row.product.exhibitor_id)}/products/${encodeURIComponent(row.product.id)}`} data-testid={`shortlist-open-product-${row.item_id}`}>
                      Open <ArrowUpRight size={13}/>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="shortlist-section" data-testid="saved-exhibitors-section">
          <div className="shortlist-section-head">
            <div className="eyebrow">SAVED EXHIBITORS</div>
            <span data-testid="saved-exhibitors-count"><b>{savedExhibitors.length}</b> item{savedExhibitors.length === 1 ? "" : "s"}</span>
          </div>
          {savedExhibitors.length === 0 ? (
            <div className="shortlist-empty" data-testid="saved-exhibitors-empty">
              You haven't saved any exhibitors yet. Tap the <span className="inline-heart"><Heart size={12}/></span> on any exhibitor card to add them here.
            </div>
          ) : (
            <div className="shortlist-list">
              {savedExhibitors.map((row) => (
                <div className="shortlist-row" key={row.id} data-testid={`shortlist-exhibitor-${row.item_id}`}>
                  <div className={`tiny-visual exhibitor-art ${row.exhibitor.accent || "blue"}`} style={{ display: "grid", placeItems: "center" }}>
                    <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18 }}>{row.exhibitor.initials}</span>
                  </div>
                  <div>
                    <h3>{row.exhibitor.name}</h3>
                    <p>{row.exhibitor.city}, {row.exhibitor.country} · {row.exhibitor.specialty}</p>
                  </div>
                  <div className="shortlist-row-actions"><LiveAlertPreference exhibitorId={row.exhibitor.id}/>
                    <button className="product-btn" onClick={() => toggle("exhibitor", row.item_id)} data-testid={`shortlist-remove-exhibitor-${row.item_id}`}>
                      Remove
                    </button>
                    <Link className="product-btn" to={`/exhibitors/${row.item_id}`} data-testid={`shortlist-open-exhibitor-${row.item_id}`}>
                      Visit booth <ArrowUpRight size={13}/>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="shortlist-section" data-testid="buyer-meetings-section">
          {meetingError && <p role="alert">{meetingError}</p>}
          <div className="shortlist-section-head">
            <div className="eyebrow">YOUR MEETINGS</div>
            <span data-testid="buyer-meetings-count">
              <b>{meetings ? meetings.length : "…"}</b> total
            </span>
          </div>
          {meetings === null ? (
            <div className="shortlist-empty">Loading your meetings…</div>
          ) : meetings.length === 0 ? (
            <div className="shortlist-empty" data-testid="buyer-meetings-empty">
              You haven't requested any meetings yet. Visit a booth and tap <b>Request Meeting</b> to get started.
            </div>
          ) : (
            <div className="lead-inbox">
              {groupMeetingRows(meetings, now).map(({meeting: m, heading}) => {
                const isTerminal = ["declined", "cancelled", "completed"].includes(m.status);
                return (
                <div className={`lead-row lead-row-${m.status}`} key={m.id} data-testid={`buyer-meeting-row-${m.id}`}>
                  {heading && <h2 data-testid={`buyer-meetings-group-${heading}`}>{heading}</h2>}
                  <MeetingCard meeting={m} counterpart={exhibitorName(m.exhibitor_id)}>
                    {state => <>
                      {m.note && <p className="lead-body">{m.note}</p>}
                      {m.status === "confirmed" && m.confirmed_time && <div className="lead-confirmed" data-testid={`buyer-meeting-${m.id}-confirmed-time`}>
                        <JoinMeetingCta state={state} meeting={m} onJoin={setActiveMeeting} sideTestId={`buyer-meeting-${m.id}`}/>
                      </div>}
                      {m.status === "rescheduled" && <small data-testid={`buyer-meeting-${m.id}-rescheduled-note`}>Awaiting confirmation for {formatDT(m.requested_time)}</small>}
                    </>}
                  </MeetingCard>
                  {!isTerminal && (
                    <div className="lead-actions">
                      {canAcceptProposal(m, user) && <>
                        <button className="product-btn" onClick={() => buyerDecision(m, "confirmed")} data-testid={`buyer-meeting-${m.id}-accept`}>Accept Proposal</button>
                        <button className="product-btn" onClick={() => buyerDecision(m, "declined")} data-testid={`buyer-meeting-${m.id}-decline`}>Decline Proposal</button>
                      </>}
                      {(
                        <button className="product-btn" onClick={() => buyerReschedule(m)} data-testid={`buyer-meeting-${m.id}-reschedule`}>
                          Propose new time <RefreshCw size={13}/>
                        </button>
                      )}
                      <button className="product-btn danger" onClick={() => buyerCancel(m)} data-testid={`buyer-meeting-${m.id}-cancel`}>
                        Cancel meeting
                      </button>
                      {m.status === "confirmed" && meetingDate(m.confirmed_time) <= now && <button className="product-btn" onClick={() => buyerDecision(m, "completed")} data-testid={`buyer-meeting-${m.id}-complete`}>Mark completed</button>}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <aside className="dash-side">
        <div className="side-stat">
          <span>Saved products</span>
          <strong data-testid="stat-saved-products">{savedProducts.length.toString().padStart(2, "0")}</strong>
          <small>Across {new Set(savedProducts.map((r) => r.product.exhibitor_id)).size} exhibitors</small>
        </div>
        <div className="side-stat">
          <span>Saved exhibitors</span>
          <strong data-testid="stat-saved-exhibitors">{savedExhibitors.length.toString().padStart(2, "0")}</strong>
          <small>Ready to reconnect anytime</small>
        </div>
        <div className="side-stat">
          <span>Confirmed meetings</span>
          <strong data-testid="stat-confirmed-meetings">
            {meetings ? meetings.filter((m) => m.status === "confirmed").length.toString().padStart(2, "0") : "—"}
          </strong>
          <small>Ready to join with a click</small>
        </div>
      </aside>
    </section>
    {activeMeeting && (
      <MeetingRoomModal
        meeting={activeMeeting}
        exhibitorName={exhibitorName(activeMeeting.exhibitor_id)}
        onClose={() => setActiveMeeting(null)}
      />
    )}
  </>;
}

const PRODUCT_TYPES = ["Natural Diamond", "Lab-Grown Diamond", "Diamond Jewellery", "Gold Jewellery", "Gemstone"];
const PRODUCT_ACCENTS = ["diamond", "ice", "gold", "sapphire"];
const emptyProductDraft = { name: "", type: "Natural Diamond", detail: "", accent: "diamond", image: "" };

const INQUIRY_STATUS_TONE = { new: "warn", read: "info", responded: "ok" };
// Join availability uses the boundaries supplied by the backend.

// Small ticker so the Join button state re-evaluates without an entire dash
// refresh. 30s cadence is enough for a ±10-minute early-join window.
function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function LeadPill({ status, tone }) {
  return <span className={`lead-pill tone-${tone}`}>{status}</span>;
}

function canAcceptProposal(meeting, user) {
  return (meeting.status === "requested" && meeting.initiated_by_role === "exhibitor" && user?.role === "buyer") || meeting.status === "rescheduled" && (meeting.proposal_author
    ? meeting.proposal_author !== user?.user_id : user?.role === "exhibitor" || user?.role === "admin");
}

function groupMeetingRows(items, now) {
  const groups = {"Upcoming meetings": [], "Pending requests and proposals": [], "Past and closed meetings": []};
  for (const m of items) {
    const ended = m.status === "confirmed" && m.join_closes_at && new Date(m.join_closes_at) <= now;
    const key = ["cancelled", "declined", "completed"].includes(m.status) || ended ? "Past and closed meetings"
      : m.status === "confirmed" ? "Upcoming meetings" : "Pending requests and proposals";
    groups[key].push(m);
  }
  return Object.entries(groups).flatMap(([heading, rows]) => rows.sort((a,b) =>
    meetingDate(a.confirmed_time || a.requested_time || a.created_at) - meetingDate(b.confirmed_time || b.requested_time || b.created_at))
    .map((meeting,index) => ({meeting, heading: index === 0 ? heading : null})));
}

function meetingDate(iso) {
  const value = /^\d{4}-\d\d-\d\dT/.test(iso) && !/(Z|[+-]\d\d:\d\d)$/i.test(iso) ? iso + "Z" : iso;
  return new Date(value);
}

function formatDT(iso) {
  if (!iso) return "—";
  try {
    const date = meetingDate(iso);
    return Number.isNaN(date.getTime()) ? "Time unavailable" : date.toLocaleString(undefined, {year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short"});
  } catch { return iso; }
}

function InquiriesPanel({ items, onUpdate, refreshing, verifiedEmails }) {
  const isVerified = (email) => email && verifiedEmails?.has(String(email).trim().toLowerCase());
  if (items === null) return <div className="product-empty">Loading inquiries…</div>;
  if (items.length === 0) return (
    <div className="product-empty" data-testid="inquiries-empty-state">
      No inquiries yet. Once buyers reach out from your booth, they'll appear here.
    </div>
  );
  return (
    <div className="lead-inbox" data-testid="inquiries-inbox">
      {items.map((i) => (
        <div className={`lead-row lead-row-${i.status}`} key={i.id} data-testid={`inquiry-row-${i.id}`}>
          <div className="lead-head">
            <div>
              <h3>
                {i.buyer_name}
                {isVerified(i.buyer_email) && <>&nbsp;<VerifiedBadge testId={`inquiry-${i.id}-verified-badge`}/></>}
              </h3>
              <p className="lead-meta">{i.buyer_email} · {formatDT(i.created_at)}</p>
            </div>
            <LeadPill status={i.status} tone={INQUIRY_STATUS_TONE[i.status] || "muted"}/>
          </div>
          <p className="lead-body">{i.message}</p>
          <div className="lead-actions">
            <button
              className="product-btn"
              disabled={i.status !== "new" || refreshing}
              onClick={() => onUpdate(i.id, { status: "read" })}
              data-testid={`inquiry-${i.id}-mark-read`}
            >
              Mark as read
            </button>
            <button
              className="product-btn"
              disabled={i.status === "responded" || refreshing}
              onClick={() => onUpdate(i.id, { status: "responded" })}
              data-testid={`inquiry-${i.id}-mark-responded`}
            >
              Mark as responded
            </button>
            <ChatButton exhibitorId={i.exhibitor_id} inquiryId={i.id}/>
            <a className="product-btn" href={`mailto:${i.buyer_email}?subject=CaratJewel Expo · Re: your inquiry`} data-testid={`inquiry-${i.id}-reply`}>
              Reply by email <ArrowUpRight size={13}/>
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

function MeetingsPanel({ items, onUpdate, onReschedule, refreshing, onJoinVideo, verifiedEmails }) {
  const {user} = useAuth();
  const isVerified = (email) => email && verifiedEmails?.has(String(email).trim().toLowerCase());
  const now = useNow();
  // Two form modes: "confirm" (exhibitor sets initial confirmed time) and
  // "reschedule" (exhibitor proposes a NEW time on an already-confirmed
  // meeting — goes through the /reschedule endpoint so the buyer is notified
  // and the meeting flips to `rescheduled`).
  const [drafts, setDrafts] = useState({}); // id -> { mode, value }
  const openDraft = (m, mode) => setDrafts((prev) => ({
    ...prev,
    [m.id]: { mode, revision: m.revision, value: toDatetimeLocal(m.confirmed_time || m.requested_time || new Date().toISOString()) },
  }));
  const cancelDraft = (id) => setDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });
  const submitDraft = async (m) => {
    const d = drafts[m.id];
    if (!d?.value) return;
    const isoTime = new Date(d.value).toISOString();
    let saved;
    if (d.mode === "reschedule") {
      saved = await onReschedule(m.id, { proposed_time: isoTime, revision: d.revision });
    } else {
      saved = await onUpdate(m.id, { status: "confirmed", confirmed_time: isoTime, revision: d.revision });
    }
    if (saved !== false) cancelDraft(m.id);
  };

  if (items === null) return <div className="product-empty">Loading meeting requests…</div>;
  if (items.length === 0) return (
    <div className="product-empty" data-testid="meetings-empty-state">
      No meeting requests yet. Buyers can request meetings from your booth or from live exhibitions.
    </div>
  );
  return (
    <div className="lead-inbox" data-testid="meetings-inbox">
      {groupMeetingRows(items, now).map(({meeting: m, heading}) => {
        const draft = drafts[m.id];
        const isTerminal = ["declined", "cancelled", "completed"].includes(m.status);
        return (
        <div className={`lead-row lead-row-${m.status}`} key={m.id} data-testid={`meeting-row-${m.id}`}>
          {heading && <h2 data-testid={`exhibitor-meetings-group-${heading}`}>{heading}</h2>}
          <MeetingCard meeting={m} counterpart={m.requester_name} badge={isVerified(m.requester_email) ? <VerifiedBadge testId={`meeting-${m.id}-verified-badge`}/> : null}>
            {state => <>
              {m.note && <p className="lead-body">{m.note}</p>}
              {m.status === "confirmed" && m.confirmed_time && <div className="lead-confirmed" data-testid={`meeting-${m.id}-confirmed-time`}>
                <JoinMeetingCta state={state} meeting={m} onJoin={onJoinVideo} sideTestId={`meeting-${m.id}-join-cta`}/>
              </div>}
              {m.status === "rescheduled" && <small data-testid={`meeting-${m.id}-rescheduled-note`}>New time proposed: {formatDT(m.requested_time)}. Confirm to lock it in.</small>}
            </>}
          </MeetingCard>
          {draft ? (
            <div className="confirm-form">
              <label>
                {draft.mode === "reschedule" ? "Propose a new meeting time" : "Confirm meeting time"}
                <input
                  type="datetime-local"
                  value={draft.value}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [m.id]: { ...prev[m.id], value: e.target.value } }))}
                  data-testid={`meeting-${m.id}-time-input`}
                />
              </label>
              <div className="lead-actions">
                <button className="product-btn" onClick={() => cancelDraft(m.id)} data-testid={`meeting-${m.id}-draft-cancel`}>Cancel</button>
                <button className="product-btn danger" onClick={() => submitDraft(m)} disabled={refreshing} data-testid={`meeting-${m.id}-draft-save`} style={{ background: "var(--ink)", color: "#fff", borderColor: "var(--ink)" }}>
                  {draft.mode === "reschedule" ? "Propose new time" : "Save time"} <ArrowUpRight size={13}/>
                </button>
              </div>
            </div>
          ) : (
            <div className="lead-actions">
              {((m.status === "requested" && m.initiated_by_role !== "exhibitor") || canAcceptProposal(m, user)) && (
                <button
                  className="product-btn"
                  disabled={refreshing}
                  onClick={() => m.status === "rescheduled" ? onUpdate(m.id, {status: "confirmed"}) : openDraft(m, "confirm")}
                  data-testid={`meeting-${m.id}-open-confirm`}
                >
                  {m.status === "rescheduled" ? "Accept Proposal" : "Confirm time"} <CalendarDays size={13}/>
                </button>
              )}
              {!isTerminal && (
                <button
                  className="product-btn"
                  disabled={refreshing}
                  onClick={() => openDraft(m, "reschedule")}
                  data-testid={`meeting-${m.id}-open-reschedule`}
                >
                  Reschedule <RefreshCw size={13}/>
                </button>
              )}
              {((m.status === "requested" && m.initiated_by_role !== "exhibitor") || canAcceptProposal(m, user)) && (
                <button
                  className="product-btn danger"
                  disabled={refreshing}
                  onClick={() => onUpdate(m.id, { status: "declined" })}
                  data-testid={`meeting-${m.id}-decline`}
                >
                  Decline
                </button>
              )}
              {!isTerminal && (
                <button
                  className="product-btn"
                  disabled={refreshing}
                  onClick={() => {
                    if (window.confirm("Cancel this meeting? The buyer will be notified and the private room will close.")) {
                      onUpdate(m.id, { status: "cancelled" });
                    }
                  }}
                  data-testid={`meeting-${m.id}-cancel`}
                >
                  Cancel meeting
                </button>
              )}
              {m.status === "confirmed" && meetingDate(m.confirmed_time) <= now && <button className="product-btn" onClick={() => onUpdate(m.id, {status: "completed"})} data-testid={`meeting-${m.id}-complete`}>Mark completed</button>}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

// Renders one of the join-state variants. The button is only clickable when
// `state.kind === "open"` — but the backend enforces the same window, so
// even a maliciously-forced click cannot obtain a token outside the window.
function JoinMeetingCta({ state, meeting, onJoin, sideTestId }) {
  const style = { marginLeft: "auto" };
  const testId = (suffix) => sideTestId ? `${sideTestId}-${suffix}` : `join-cta-${meeting.id}-${suffix}`;
  if (state.kind === "open") {
    return (
      <button
        className="product-btn"
        onClick={() => onJoin && onJoin(meeting)}
        data-testid={`meeting-${meeting.id}-join-video`}
        style={{ ...style, background: "var(--ink)", color: "#fff", borderColor: "var(--ink)" }}
      >
        <Video size={13}/>&nbsp;Join Private Meeting
      </button>
    );
  }
  if (state.kind === "not_yet") {
    const label = state.minsUntil >= 60
      ? `Join available in ${Math.floor(state.minsUntil / 60)}h ${state.minsUntil % 60}m`
      : `Join available in ${state.minsUntil} min`;
    return <span className="join-hint" data-testid={testId("not-yet")} style={style}>{label}</span>;
  }
  if (state.kind === "ended") return <span className="join-hint" data-testid={testId("ended")} style={style}>Meeting ended</span>;
  if (state.kind === "cancelled") return <span className="join-hint" data-testid={testId("cancelled")} style={style}>Cancelled</span>;
  if (state.kind === "rescheduled") return <span className="join-hint" data-testid={testId("rescheduled")} style={style}>Reschedule pending</span>;
  if (state.kind === "completed") return <span className="join-hint" style={style}>Completed</span>;
  return <span className="join-hint" style={style}>Awaiting confirmation</span>;
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = meetingDate(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EXH_TABS = [
  ["catalogue", "Catalogue"],
  ["commercial", "Packages & payments"],
  ["live", "Live booth"],
  ["inquiries", "Inquiries"],
  ["meetings", "Meetings"],
];

function ExhibitorDashboard({ displayName }) {
  const { user, refresh: refreshAccess } = useAuth();
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [accessMessage, setAccessMessage] = useState("");
  const { exhibitors, exhibitions } = useData();
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    const requested = new URLSearchParams(location.search).get("tab");
    return EXH_TABS.some(([id]) => id === requested) ? requested : "catalogue";
  });
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("tab");
    if (EXH_TABS.some(([id]) => id === requested)) setTab(requested);
  }, [location.search]);
  const [products, setProducts] = useState(null);
  const [inquiries, setInquiries] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null); // { id?, ...fields } — null hides the form
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [liveOverride, setLiveOverride] = useState(null); // null = trust myExhibitor, bool = local override
  const [verifiedEmails, setVerifiedEmails] = useState(new Set());
  const notConfigured = user && !user.exhibitor_id;
  const myExhibitor = user?.exhibitor_id ? exhibitors.find((e) => e.id === user.exhibitor_id) : null;
  const isLive = liveOverride ?? !!myExhibitor?.live;
  useEffect(() => { if (tab !== "live") setLiveOverride(null); }, [tab]);
  const hasLiveExhibition = exhibitions.some(e => liveWindowOpen(e));

  const load = useCallback(async () => {
    if (notConfigured) return;
    setRefreshing(true);
    const [p, i, m, v] = await Promise.allSettled([
      api.myProducts(), api.myInquiries(), api.myMeetings(), api.verifiedBuyerEmails(),
    ]);
    if (p.status === "fulfilled") setProducts(p.value);
    if (i.status === "fulfilled") setInquiries(i.value);
    if (m.status === "fulfilled") setMeetings(m.value);
    if (v.status === "fulfilled") setVerifiedEmails(new Set((v.value?.emails || []).map((e) => e.toLowerCase())));
    if (p.status === "rejected") setError(p.reason?.message || "Could not load your data.");
    setRefreshing(false);
  }, [notConfigured, user?.exhibitor_id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let alive = true;
    const refreshMeetings = async () => {
      try { const rows = await api.myMeetings(); if (alive) setMeetings(rows); }
      catch { /* Preserve the last known meetings; mutations surface errors. */ }
    };
    const timer = setInterval(refreshMeetings, 15000);
    window.addEventListener("cjx-meeting-changed", refreshMeetings);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("cjx-meeting-changed", refreshMeetings); };
  }, []);

  const openAdd = () => { setTab("catalogue"); setDraft({ ...emptyProductDraft }); };
  const openEdit = (p) => { setTab("catalogue"); setDraft({
    id: p.id, name: p.name, type: p.type, detail: p.detail,
    accent: p.accent || "diamond", image: p.image || "",
  }); };
  const cancel = () => setDraft(null);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(), type: draft.type, detail: draft.detail.trim(),
        accent: draft.accent, image: draft.image?.trim() || null,
      };
      if (draft.id) await api.updateProduct(draft.id, payload);
      else await api.createProduct(payload);
      setDraft(null);
      await load();
    } catch (e) { alert(e.message || "Could not save the product."); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.deleteProduct(p.id); await load(); }
    catch (e) { alert(e.message || "Could not delete the product."); }
  };

  const updateInquiry = async (id, patch) => {
    try { await api.updateInquiry(id, patch); await load(); }
    catch (e) { alert(e.message || "Could not update the inquiry."); }
  };
  const updateMeeting = async (id, patch) => {
    try { await api.updateMeeting(id, {...patch, revision: patch.revision ?? meetings?.find(m => m.id === id)?.revision}); window.dispatchEvent(new Event("cjx-meeting-changed")); if (patch.status !== "confirmed") setActiveMeeting(null); await load(); return true; }
    catch (e) { alert(e.message || "Could not update the meeting."); return false; }
  };
  const rescheduleMeeting = async (id, payload) => {
    try { await api.rescheduleMeeting(id, {...payload, revision: payload.revision ?? meetings?.find(m => m.id === id)?.revision}); window.dispatchEvent(new Event("cjx-meeting-changed")); setActiveMeeting(null); await load(); return true; }
    catch (e) { alert(e.message || "Could not reschedule the meeting."); return false; }
  };

  const newInquiries = (inquiries || []).filter((i) => i.status === "new").length;
  const openMeetings = (meetings || []).filter((m) => m.status === "requested" || m.status === "rescheduled").length;
  const productsCount = products?.length ?? null;

  return <>
    <div className="dashboard-head">
      <div>
        <div className="eyebrow">EXHIBITOR WORKSPACE</div>
        <h1>Good morning, <em data-testid="dashboard-user-name">{displayName}.</em></h1>
      </div>
      {!notConfigured && (
        <button className="button button-dark" onClick={openAdd} data-testid="dashboard-primary-button">
          Add a product <ArrowUpRight size={16}/>
        </button>
      )}
    </div>

    {notConfigured && (
      <section className="content-section" data-testid="exhibitor-not-configured">
        <div className="eyebrow">BOOTH SETUP PENDING</div>
        <h2 style={{ fontSize: "clamp(28px,3.4vw,44px)", fontWeight: 500, margin: "12px 0 18px" }}>
          Your booth isn't<br/><em>linked yet.</em>
        </h2>
        <p style={{ maxWidth: 480, fontSize: 13, lineHeight: 1.8, color: "#777" }}>
          An administrator still needs to link your account to a company profile.
          Once linked, you can add products and manage buyer leads right here.
        </p>
        <button className="button button-outline" disabled={checkingAccess} data-testid="refresh-booth-access"
          onClick={async () => {
            setCheckingAccess(true); setAccessMessage("");
            try {
              const account = await refreshAccess();
              setAccessMessage(account?.exhibitor_id ? "" : "Booth setup is still pending. Ask your administrator to check the application setup.");
            } finally { setCheckingAccess(false); }
          }}>{checkingAccess ? "Checking…" : "Refresh booth access"}</button>
        {accessMessage && <p role="status">{accessMessage}</p>}
      </section>
    )}

    {!notConfigured && (
      <>
        <div className="exh-tabbar" data-testid="exhibitor-tabbar">
          {EXH_TABS.map(([id, label]) => {
            const source = id === "inquiries" ? inquiries : id === "meetings" ? meetings : ["live", "commercial"].includes(id) ? null : products;
            const badge = id === "live"
              ? (isLive ? "LIVE" : null)
              : source === null ? null
              : id === "inquiries" ? newInquiries
              : id === "meetings" ? openMeetings
              : source.length;
            return (
              <button
                key={id}
                className={tab === id ? "exh-tab active" : "exh-tab"}
                onClick={() => setTab(id)}
                data-testid={`exhibitor-tab-${id}`}
              >
                {label}
                {badge !== null && (
                  <span className={`exh-tab-badge${((id === "inquiries" && badge > 0) || id === "live") ? " tone-warn" : ""}`}>{badge}</span>
                )}
              </button>
            );
          })}
        </div>

        <section className="dashboard-grid exhibitor-grid-layout">
          <div className="dash-main">
            <EntitlementPanel/>
            {tab === "catalogue" && (
              <>
                <div className="dash-title">
                  <h2>Your catalogue</h2>
                  <span data-testid="exhibitor-product-count">{products ? `${products.length} product${products.length === 1 ? "" : "s"}` : "loading…"}</span>
                </div>
                {draft && (
                  <form className="product-form" onSubmit={save} data-testid="product-form">
                    <div className="eyebrow">{draft.id ? "EDIT PRODUCT" : "NEW PRODUCT"}</div>
                    <div className="product-form-grid">
                      <label>Name<input required value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Oval Brilliant 2.03ct" data-testid="product-name-input"/></label>
                      <label>Type
                        <select value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })} data-testid="product-type-select">
                          {PRODUCT_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </label>
                      <label className="full">Detail<input required value={draft.detail} onChange={e => setDraft({ ...draft, detail: e.target.value })} placeholder="e.g. G / VS1 · IGI certified" data-testid="product-detail-input"/></label>
                      <label>Accent
                        <select value={draft.accent} onChange={e => setDraft({ ...draft, accent: e.target.value })} data-testid="product-accent-select">
                          {PRODUCT_ACCENTS.map(a => <option key={a}>{a}</option>)}
                        </select>
                      </label>
                      <label>Image URL<input type="url" value={draft.image} onChange={e => setDraft({ ...draft, image: e.target.value })} placeholder="https://…" data-testid="product-image-input"/></label>
                    </div>
                    <div className="product-form-actions">
                      <button type="button" className="button button-outline" onClick={cancel} data-testid="product-form-cancel">Cancel</button>
                      <button type="submit" className="button button-dark" disabled={saving} data-testid="product-form-submit">
                        {saving ? "Saving…" : draft.id ? "Save changes" : "Add product"} <ArrowUpRight size={16}/>
                      </button>
                    </div>
                  </form>
                )}
                {error && <div className="product-error" data-testid="product-error">{error}</div>}
                {products === null && !error && <div className="product-empty">Loading your products…</div>}
                {products !== null && products.length === 0 && !draft && (
                  <div className="product-empty" data-testid="product-empty-state">
                    You haven't added any products yet. Click <b>Add a product</b> to begin.
                  </div>
                )}
                {products && products.length > 0 && (
                  <div className="product-manage-list" data-testid="product-manage-list">
                    {products.map(p => (
                      <div className="product-manage-row" key={p.id} data-testid={`product-manage-row-${p.id}`}>
                        <div className={`tiny-visual ${p.accent || "diamond"}`}/>
                        <div className="product-manage-info">
                          <h3>{p.name}</h3>
                          <p>{p.type} · {p.detail}</p>
                        </div>
                        <div className="product-manage-actions">
                          <button className="product-btn" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>Edit</button>
                          <button className="product-btn danger" onClick={() => remove(p)} data-testid={`delete-product-${p.id}`}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {tab === "live" && (
              <>
                <div className="dash-title">
                  <h2>Live booth</h2>
                  <span data-testid="exhibitor-live-status">
                    {isLive ? "on air" : hasLiveExhibition ? "ready" : "closed"}
                  </span>
                </div>
                {myExhibitor ? (
                  <BroadcastControls
                    exhibitor={myExhibitor}
                    exhibitions={exhibitions}
                    onLiveChange={(next) => setLiveOverride(next)}
                  />
                ) : (
                  <div className="product-empty" data-testid="live-not-linked">
                    Your booth isn't linked yet. Broadcasting will appear here once an
                    administrator links your account to a company profile.
                  </div>
                )}
              </>
            )}
            {tab === "commercial" && <ExhibitorCommercialPanel/>}
            {tab === "inquiries" && (
              <>
                <div className="dash-title">
                  <h2>Inquiries</h2>
                  <span data-testid="exhibitor-inquiry-count">{inquiries ? `${newInquiries} new · ${inquiries.length} total` : "loading…"}</span>
                </div>
                <InquiriesPanel items={inquiries} onUpdate={updateInquiry} refreshing={refreshing} verifiedEmails={verifiedEmails}/>
              </>
            )}
            {tab === "meetings" && (
              <>
                <div className="dash-title">
                  <h2>Meeting requests</h2>
                  <span data-testid="exhibitor-meeting-count">{meetings ? `${openMeetings} awaiting · ${meetings.length} total` : "loading…"}</span>
                </div>
                <MeetingsPanel
                  items={meetings}
                  onUpdate={updateMeeting}
                  onReschedule={rescheduleMeeting}
                  refreshing={refreshing}
                  onJoinVideo={(m) => setActiveMeeting(m)}
                  verifiedEmails={verifiedEmails}
                />
              </>
            )}
          </div>
          <aside className="dash-side">
            <div className="side-stat"><span>Products live</span><strong data-testid="exhibitor-stat-products">{products ? products.length.toString().padStart(2, "0") : "—"}</strong><small>Visible on the exhibition</small></div>
            <div className="side-stat"><span>New inquiries</span><strong data-testid="exhibitor-stat-new-inquiries">{inquiries ? newInquiries.toString().padStart(2, "0") : "—"}</strong><small>Awaiting a first reply</small></div>
            <div className="side-stat"><span>Open meetings</span><strong data-testid="exhibitor-stat-open-meetings">{meetings ? openMeetings.toString().padStart(2, "0") : "—"}</strong><small>Not confirmed yet</small></div>
            <div className="upcoming">
              <div className="eyebrow">TIP</div>
              <h3><Sparkles size={17}/> Fast replies win business</h3>
              <p>Inquiries answered within a day convert 3× more often. Aim to close the loop today.</p>
            </div>
          </aside>
        </section>
      </>
    )}
    {activeMeeting && (
      <MeetingRoomModal
        meeting={activeMeeting}
        exhibitorName={activeMeeting.requester_name || "your buyer"}
        onClose={() => setActiveMeeting(null)}
      />
    )}
  </>;
}

function Dashboard({ type = "buyer" }) {
  const { user } = useAuth();
  const displayName = user?.name || (type === "buyer" ? "Elena" : "your workspace");
  return type === "buyer"
    ? <BuyerDashboard key={`${user?.user_id}:${user?.role}`} displayName={displayName}/>
    : <ExhibitorDashboard key={`${user?.user_id}:${user?.exhibitor_id}`} displayName={displayName}/>;
}

function MeetingPopups() {
  const {user} = useAuth();
  return user ? <AccountMeetingPopups key={`${user.user_id}:${user.role}:${user.exhibitor_id}`} user={user}/> : null;
}

function AccountMeetingPopups({user}) {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    let alive = true, timer;
    const poll = async () => {
      try {
        const rows = await api.meetingEvents();
        for (const event of Array.isArray(rows) ? rows : []) {
          if (!alive) return;
          const storageKey = `meeting-popup:${user.user_id}:${event.id}`;
          let shown = false;
          try { shown = localStorage.getItem(storageKey) === "shown"; } catch {}
          if (shown) continue;
          const claim = await api.claimMeetingEvent(event.id);
          if (!alive || !claim.claimed) continue;
          try { localStorage.setItem(storageKey, "shown"); } catch {}
          setEvents(previous => previous.some(e => e.id === event.id) ? previous : [...previous, event]);
        }
      } catch { /* A later poll retries; do not clear existing critical notices. */ }
      finally { if (alive) timer = setTimeout(poll, 15000); }
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [user.user_id]);
  if (!events.length) return null;
  const event = events[0];
  const remaining = event.when ? Math.max(1, Math.ceil((new Date(event.when) - new Date()) / 60000)) : null;
  return <aside role="status" aria-live="polite" data-testid="meeting-popup" style={{position:"fixed",right:20,bottom:20,zIndex:1500,background:"white",border:"1px solid #ddd",padding:20,maxWidth:"calc(100vw - 40px)",boxShadow:"0 4px 24px #0002"}}>
    <strong>{event.event === "soon" && remaining ? `Meeting starts in ${remaining} minutes` : event.title}</strong>
    {event.when && <p>{formatDT(event.when)}</p>}
    <Link className="product-btn" to={`/dashboard/${user.role === "exhibitor" ? "exhibitor?tab=meetings" : "buyer"}`} onClick={() => setEvents(previous => previous.slice(1))}>Open Meetings</Link>
    <button className="product-btn" onClick={() => setEvents(previous => previous.slice(1))} data-testid="meeting-popup-dismiss">Dismiss</button>
  </aside>;
}

// ---------- Router shell ----------
function Shell() {
  const location = useLocation();
  // Session-id callback must be handled BEFORE anything else — sync render check
  // per the Emergent Google Auth playbook.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback/>;
  }
  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin" element={
          <ProtectedRoute roles={["admin"]}><Admin/></ProtectedRoute>
        }/>
      </Routes>
    );
  }
  return <>
    <Header/>
    <main className="public-site">
      <Routes>
        <Route path="/" element={<Home/>}/>
        <Route path="/explore" element={<Explore/>}/>
        <Route path="/exhibitions" element={<ExhibitionCalendar/>}/>
        <Route path="/exhibitions/:id" element={<ExhibitionLanding/>}/>
        <Route path="/exhibitors" element={<Exhibitors/>}/>
        <Route path="/exhibitors/:id" element={<Booth/>}/>
        <Route path="/products" element={<ProductDirectory/>}/>
        <Route path="/products/:productId" element={<ProductDetail/>}/>
        <Route path="/exhibitors/:exhibitorId/products/:productId" element={<ProductDetail/>}/>
        <Route path="/about" element={<ContentPage slug="about"/>}/>
        <Route path="/contact" element={<ContentPage slug="contact"/>}/>
        <Route path="/legal/:slug" element={<ContentPage/>}/>
        <Route path="/pricing" element={<Pricing/>}/>
        <Route path="/exhibit" element={<Exhibit/>}/>
        <Route path="/login" element={<Login/>}/>
        <Route path="/verify" element={
          <ProtectedRoute roles={["buyer", "admin", "exhibitor"]}><Verify/></ProtectedRoute>
        }/>
        <Route path="/dashboard/buyer" element={
          <ProtectedRoute roles={["buyer", "admin", "exhibitor"]}><Dashboard/></ProtectedRoute>
        }/>
        <Route path="/dashboard/exhibitor" element={
          <ProtectedRoute roles={["exhibitor", "admin"]}><Dashboard type="exhibitor"/></ProtectedRoute>
        }/>
        <Route path="*" element={<Home/>}/>
      </Routes>
    </main>
    <ContentFooter/>
  </>;
}

const refinementStyles = `.live-section{border-top:1px solid var(--line)}.live-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}.live-row{border:1px solid var(--line);padding:14px;display:grid;grid-template-columns:64px 1fr;gap:14px;align-items:center}.live-row .button{grid-column:1/-1;width:100%}.live-logo{height:64px!important}.live-info h3{font-size:14px;margin:6px 0}.live-info p{font-size:10px;color:#888;margin:0}.live-status,.booth-live{font:9px 'DM Mono',monospace;letter-spacing:.08em;color:#bd6b58}.live-status i,.booth-live i,.status-chip i,.exhibition-status i,.live-floor-label i{display:inline-block;width:6px;height:6px;border-radius:50%;background:currentColor;margin-right:6px}.status-chip{position:absolute;left:12px;bottom:12px;font:9px 'DM Mono',monospace;letter-spacing:.08em;color:#777;background:rgba(255,255,255,.82);padding:6px 8px}.status-chip.live{color:#a85e50}.verified-badge{display:inline-flex;vertical-align:middle;margin-left:7px;color:#638d85}.light-badge{color:#fff}.booth-identity{display:flex;align-items:center;gap:18px}.booth-logo{width:52px;height:52px;border:1px solid rgba(255,255,255,.65);border-radius:50%;display:grid;place-items:center;font:20px 'Playfair Display',serif}.booth-identity h1{display:flex;align-items:center;gap:5px}.booth-live{margin-top:22px;color:rgba(255,255,255,.88)}.booth-live.live{color:#d1efdf}.booth-specialties{display:flex;flex-wrap:wrap;gap:8px;margin:18px 0 25px}.booth-specialties span{font:9px 'DM Mono',monospace;border:1px solid var(--line);padding:8px 10px;color:#777}.booth-actions{display:flex;flex-wrap:wrap;gap:10px}.button-outline{border-color:var(--ink);background:transparent}.product-visual.has-image{background-size:cover;background-position:center}.product-visual.has-image:after{content:'';position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.2),transparent 35%)}.pricing-section{padding:0 10vw 90px}.pricing-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}.plan-card{border:1px solid var(--line);padding:28px;min-height:510px;display:flex;flex-direction:column;background:#fff}.plan-card.popular{border:1px solid var(--ink);box-shadow:0 12px 35px rgba(0,0,0,.06)}.plan-card-top{display:flex;justify-content:space-between;align-items:start;border-bottom:1px solid var(--line);padding-bottom:25px}.plan-card-top .eyebrow{margin-bottom:14px}.plan-card h2{font:500 34px 'Manrope',sans-serif;margin:0;letter-spacing:-.04em}.plan-card h2 small{font:10px 'DM Mono',monospace;color:#888;margin-left:5px;letter-spacing:0}.popular-label{font:9px 'DM Mono',monospace;background:var(--ink);color:#fff;padding:7px 8px}.plan-features{list-style:none;padding:22px 0 0;margin:0;flex:1}.plan-features li{font-size:12px;padding:9px 0;border-bottom:1px solid #f0f0ed;color:#555}.plan-features li:before{content:'+';font-family:'DM Mono',monospace;color:#999;margin-right:10px}.plan-card .full-button{margin-top:25px}.buyer-free-note{display:flex;gap:12px;align-items:flex-start;border-top:1px solid var(--line);border-bottom:1px solid var(--line);padding:25px 0;margin-top:36px}.buyer-free-note svg{color:#678a84}.buyer-free-note strong,.buyer-free-note span{display:block}.buyer-free-note strong{font-size:13px;margin-bottom:5px}.buyer-free-note span{font-size:11px;color:#777}.video-explainer{border-top:1px solid var(--line);display:grid;grid-template-columns:1fr 1fr;gap:12vw}.video-explainer-copy{display:grid;gap:22px}.room-explainer{display:flex;gap:15px;border-top:1px solid var(--line);padding-top:18px}.room-icon{width:34px;height:34px;border:1px solid var(--line);display:grid;place-items:center;flex:none}.room-icon.public{color:#638d85}.room-icon.private{color:#8a7657}.room-explainer h3{margin:0 0 8px;font-size:14px}.room-explainer p{font-size:12px;line-height:1.7;color:#777;margin:0;max-width:400px}.comparison-section{border-top:1px solid var(--line)}.comparison-table{border-top:1px solid var(--ink);font-size:12px}.comparison-row{display:grid;grid-template-columns:2fr repeat(3,1fr);border-bottom:1px solid var(--line);min-height:52px;align-items:center}.comparison-row span,.comparison-row strong{padding:0 14px}.comparison-row span:not(:first-child),.comparison-row strong:not(:first-child){border-left:1px solid var(--line)}.comparison-head{font-size:11px;background:#f1f1ee}.comparison-head strong{font-weight:600}.apply-pricing-preview{padding:0 10vw 75px;display:grid;grid-template-columns:.8fr 1.4fr;gap:8vw;align-items:end;border-bottom:1px solid var(--line)}.apply-pricing-preview h2{font-size:34px;line-height:1.05;font-weight:500;margin:0}.preview-plan-row{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}.preview-plan{background:transparent;border:1px solid var(--line);padding:15px;text-align:left;display:grid;gap:8px}.preview-plan.selected{background:var(--ink);color:#fff;border-color:var(--ink)}.preview-plan span{font:9px 'DM Mono',monospace;letter-spacing:.08em}.preview-plan strong{font-size:14px}.preview-plan small{font:9px 'DM Mono',monospace;color:#999;margin-left:3px}.preview-plan.selected small{color:#bbb}.apply-pricing-preview .arrow-link{grid-column:2;justify-self:start;margin-top:18px}.selected-plan-note{border:1px solid var(--line);padding:13px;margin:22px 0 4px}.selected-plan-note span,.selected-plan-note strong{display:block}.selected-plan-note span{font:9px 'DM Mono',monospace;color:#888;text-transform:uppercase}.selected-plan-note strong{font-size:13px;margin-top:5px}.calendar-hero{border-top:1px solid var(--line);border-bottom:1px solid var(--line);display:grid;grid-template-columns:1.4fr .8fr auto;gap:40px;align-items:end}.calendar-hero h2{font-size:32px;font-weight:500;margin:0 0 8px}.calendar-hero p{font-size:12px;color:#777;margin:0}.calendar-countdown{border-left:1px solid var(--line);padding-left:28px}.calendar-countdown span{display:block;font:9px 'DM Mono',monospace;color:#888;margin-bottom:12px}.calendar-countdown strong{font:28px 'Playfair Display',serif;font-weight:500}.exhibition-grid-list{display:grid;grid-template-columns:repeat(2,1fr);gap:18px}.exhibition-card{border-top:1px solid var(--ink);padding:23px 0 18px}.exhibition-card-top{display:flex;justify-content:space-between;gap:15px}.exhibition-card h2{font-size:25px;font-weight:500;margin:0}.exhibition-card .eyebrow{margin-bottom:12px}.exhibition-card p{font-size:12px;line-height:1.7;color:#777;max-width:500px;margin:18px 0}.exhibition-status{font:9px 'DM Mono',monospace;color:#888;white-space:nowrap;padding-top:4px}.exhibition-status.live{color:#b96556}.exhibition-card-meta{display:flex;gap:20px;font:10px 'DM Mono',monospace;color:#888;margin:22px 0}.exhibition-card-actions{display:flex;justify-content:space-between;align-items:center}.exhibition-card-actions .button{padding:10px 13px}.calendar-note{border-top:1px solid var(--line);display:grid;grid-template-columns:1fr 1fr;gap:12vw}.calendar-note h2{font-size:38px;font-weight:500;margin:0}.calendar-note p{font-size:13px;line-height:1.8;color:#777;max-width:390px;margin:30px 0 0}.exhibition-landing-hero{min-height:570px;background:linear-gradient(120deg,#2b3736,#a9bbb6);color:#fff;display:flex;align-items:end;padding:80px 10vw;position:relative;overflow:hidden}.exhibition-landing-hero:after{content:'';position:absolute;inset:0;background:linear-gradient(90deg,rgba(10,14,14,.62),rgba(10,14,14,.12))}.exhibition-landing-hero.is-live{background:linear-gradient(120deg,#263b39,#b6cec7)}.exhibition-landing-copy{position:relative;z-index:1}.exhibition-landing-copy h1{font-size:clamp(45px,6vw,82px);line-height:1.02;font-weight:500;margin:20px 0}.exhibition-landing-copy h1 em{font-family:'Playfair Display',serif}.exhibition-landing-copy p{font-size:13px;color:rgba(255,255,255,.82)}.exhibition-landing-copy .exhibition-status{display:block;color:#fff}.exhibition-landing-copy .exhibition-status.live{color:#ddf1e8}.exhibition-overview{display:grid;grid-template-columns:1fr 1fr;gap:12vw}.exhibition-overview h2{font-size:46px;font-weight:500;margin:0}.exhibition-overview p{font-size:13px;line-height:1.8;color:#777;max-width:370px;margin-top:28px}.exhibition-tools{display:grid;gap:18px}.tool-row{display:flex;gap:14px;border-top:1px solid var(--line);padding-top:18px}.tool-row svg{color:#688c86;flex:none}.tool-row strong,.tool-row span{display:block}.tool-row strong{font-size:13px;margin-bottom:7px}.tool-row span{font-size:11px;color:#777;line-height:1.6}.live-floor{border-top:1px solid var(--line)}.live-floor-label{font:9px 'DM Mono',monospace;color:#b96556}.show-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:42px}.exhibition-ended-note{border:1px solid rgba(255,255,255,.35);padding:22px 24px;max-width:540px;display:grid;gap:10px;background:rgba(255,255,255,.06);backdrop-filter:blur(4px)}.exhibition-ended-note strong{font:500 15px 'Manrope',sans-serif;letter-spacing:.02em}.exhibition-ended-note span{font-size:12px;line-height:1.7;color:rgba(255,255,255,.82)}.arrow-link.light{color:#fff;border-bottom-color:rgba(255,255,255,.5);width:fit-content}.exhibitor-grid-layout{grid-template-columns:1.55fr .6fr}.product-form{border:1px solid var(--ink);padding:22px 24px;margin-bottom:24px;background:#fff}.product-form .eyebrow{margin-bottom:14px}.product-form-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}.product-form-grid label{display:block;font:10px 'DM Mono',monospace;letter-spacing:.08em;color:#555}.product-form-grid label.full{grid-column:1/-1}.product-form-grid input,.product-form-grid select{display:block;width:100%;padding:12px 0;border:0;border-bottom:1px solid #aaa;background:transparent;outline:0;font-size:13px;margin-top:6px;font-family:inherit}.product-form-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:20px}.product-error{font:10px 'DM Mono',monospace;color:#b04a3a;padding:14px 0}.product-empty{border:1px dashed var(--line);padding:26px;text-align:center;font-size:12px;color:#888}.product-empty b{color:var(--ink)}.exh-tabbar{display:flex;gap:24px;border-bottom:1px solid var(--line);margin:24px 10vw 0;padding-bottom:0}.exh-tab{background:none;border:0;padding:14px 0;font-size:12px;color:#777;font-family:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:8px;border-bottom:2px solid transparent;margin-bottom:-1px;letter-spacing:.01em}.exh-tab:hover{color:var(--ink)}.exh-tab.active{color:var(--ink);border-bottom-color:var(--ink)}.exh-tab-badge{font:9px 'DM Mono',monospace;color:#666;background:#f0f0ed;padding:2px 7px;border-radius:2px;letter-spacing:.06em}.exh-tab.active .exh-tab-badge{background:var(--ink);color:#fff}.exh-tab-badge.tone-warn{background:#c4664f;color:#fff}.lead-inbox{display:grid;gap:14px}.lead-row{border:1px solid var(--line);background:#fff;padding:18px 20px;display:grid;gap:12px}.lead-row-new{border-left:3px solid #c4664f}.lead-row-requested{border-left:3px solid #c4664f}.lead-row-confirmed{border-left:3px solid #688c86}.lead-row-declined{border-left:3px solid #b04a3a;background:#fbf4f2}.lead-row-responded{border-left:3px solid #688c86}.lead-row-read{border-left:3px solid #b3b3ac}.lead-head{display:flex;justify-content:space-between;gap:14px;align-items:start}.lead-head h3{font-size:14px;font-weight:600;margin:0}.lead-meta{font:10px 'DM Mono',monospace;color:#888;margin:5px 0 0}.lead-body{font-size:13px;line-height:1.7;color:#333;margin:0;border-top:1px solid var(--line);padding-top:12px}.lead-confirmed{font-size:12px;color:#4c6c56;display:flex;align-items:center;gap:6px;background:#eef4ef;border:1px solid #cddcd1;padding:8px 12px}.lead-confirmed strong{font-weight:600}.lead-actions{display:flex;flex-wrap:wrap;gap:8px}.lead-pill{font:9px 'DM Mono',monospace;padding:5px 8px;border:1px solid var(--line);letter-spacing:.05em;text-transform:uppercase;white-space:nowrap}.lead-pill.tone-ok{border-color:#8ba695;color:#4c6c56;background:#eef4ef}.lead-pill.tone-warn{border-color:#c9a06f;color:#7c5729;background:#f7efe0}.lead-pill.tone-danger{border-color:#c98d84;color:#8a3f34;background:#f6e4e0}.lead-pill.tone-info{border-color:#8ea4b3;color:#3e5a70;background:#e9eef4}.lead-pill.tone-muted{border-color:var(--line);color:#666;background:#f4f4f0}.confirm-form{border-top:1px solid var(--line);padding-top:12px;display:grid;gap:10px}.confirm-form label{display:block;font:10px 'DM Mono',monospace;letter-spacing:.06em;color:#555}.confirm-form input{display:block;width:100%;max-width:280px;padding:11px 0;border:0;border-bottom:1px solid #aaa;background:transparent;font-size:13px;margin-top:6px;font-family:inherit}.product-manage-list{display:grid;gap:10px}.product-manage-row{border-top:1px solid var(--line);padding:16px 0;display:grid;grid-template-columns:56px 1fr auto;gap:16px;align-items:center}.product-manage-row:first-child{border-top:1px solid var(--ink)}.product-manage-info h3{font-size:13px;font-weight:600;margin:0 0 5px}.product-manage-info p{font-size:11px;color:#888;margin:0}.product-manage-actions{display:flex;gap:6px}.product-btn{border:1px solid var(--line);background:#fff;padding:6px 10px;font-size:11px;display:inline-flex;align-items:center;gap:6px;cursor:pointer;color:var(--ink);text-decoration:none}.product-btn:hover{border-color:var(--ink)}.product-btn.danger:hover{background:#f6e4e0;color:#7a382e;border-color:#c98d84}.save-button.exhibitor-save{right:12px;top:12px;color:#3a4948}.save-button.exhibitor-save.saved{color:#c36663}.buyer-shortlist-layout{grid-template-columns:1.55fr .6fr}.shortlist-section{margin-top:32px}.shortlist-section:first-child{margin-top:0}.shortlist-section-head{display:flex;justify-content:space-between;align-items:end;margin-bottom:14px}.shortlist-section-head span{font:10px 'DM Mono',monospace;color:#555;letter-spacing:.05em}.shortlist-section-head span b{color:var(--ink);font-weight:600;margin-right:3px}.inline-heart{display:inline-flex;vertical-align:middle;margin:0 2px;color:#c36663}.shortlist-empty{border:1px dashed var(--line);padding:22px;text-align:center;font-size:12px;color:#666;line-height:1.7}.shortlist-row{grid-template-columns:56px 1fr auto;gap:16px}.shortlist-row-actions{display:flex;gap:6px}@media(max-width:850px){.live-grid,.pricing-grid{grid-template-columns:1fr}.video-explainer{grid-template-columns:1fr;gap:45px}.apply-pricing-preview{grid-template-columns:1fr;gap:30px}.apply-pricing-preview .arrow-link{grid-column:1}.comparison-table{font-size:10px}.comparison-row span,.comparison-row strong{padding:0 7px}.calendar-hero,.calendar-note,.exhibition-overview{grid-template-columns:1fr;gap:30px}.calendar-countdown{border-left:0;border-top:1px solid var(--line);padding:20px 0 0}.exhibition-grid-list{grid-template-columns:1fr}.exhibition-landing-hero{min-height:620px;padding:60px 7vw}.exhibition-overview h2,.calendar-note h2{font-size:38px}.exhibitor-grid-layout{grid-template-columns:1fr}.product-form-grid{grid-template-columns:1fr}.product-manage-row{grid-template-columns:48px 1fr}.product-manage-actions{grid-column:1/-1;justify-content:flex-end}.buyer-shortlist-layout{grid-template-columns:1fr}.shortlist-row{grid-template-columns:48px 1fr}.shortlist-row-actions{grid-column:1/-1;justify-content:flex-end}}`;

export default function App() {
  return (
    <BrowserRouter>
      <style>{refinementStyles}</style>
      <AuthProvider>
        <MeetingPopups/>
        <CommunicationCenter/>
        <SiteProvider><DataProvider>
          <ShortlistProvider>
            <Shell/>
          </ShortlistProvider>
        </DataProvider></SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
